import { Button, Modal, useToast } from "@scoach/ui";
import {
  Check,
  Eye,
  Doc,
  Globe,
  Link as LinkIcon,
  Plus,
  Spark,
  Trash,
  Users,
} from "@scoach/ui/icons";
import { DEFAULT_COMPANY_ROLES } from "@scoach/types";
import { useEffect, useMemo, useRef, useState } from "react";
import { ErrorBoundary } from "../../components/ErrorBoundary.tsx";
import { connectGoogleDrive } from "../../lib/firebase.ts";
import { safeHref } from "../../lib/safeHref.ts";
import { getTenantSubdomain, slugifySubdomain } from "../../lib/subdomain.ts";
import { useAuthStore } from "../auth/store.ts";
import { DashHeader } from "../dashboard/components/DashHeader.tsx";
import { companyApi, type CompanyDataResponse, type MemberOverviewResponse } from "./api.ts";
import { tasksApi } from "../tasks/api.ts";
import type { ConnectorType, TaskView, TeamMember } from "@scoach/types";

function cleanRoleName(r: string): string {
  if (!r) return "";
  let name = r.trim();
  if (name.includes("אנשי מכירות וסגירת עסקאות")) return "Sales / Account Executive (AE)";
  if (name.includes("תמיכה, שירות והצלחת לקוחות")) return "Customer Support & Success";
  if (name.includes("מהנדסי מכירות ופריסייל")) return "Pre-Sales / Solutions Engineer";
  if (name.includes("פיתוח עסקי ופניות ראשוניות")) return "BDR (Business Development)";
  if (name.includes("שימור וניהול לקוחות")) return "Retention & Account Management";
  if (name.includes("הנהלה וניהול צוותים")) return "Executive / Management";
  name = name.replace(/\s*\([\u0590-\u05FF\s,.\/-]+\)/g, "").trim();
  return name;
}

export function CompanyScreen() {
  const toast = useToast();
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const googleToken = useAuthStore((s) => s.googleAccessToken);
  const setGoogleToken = useAuthStore((s) => s.setGoogleAccessToken);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<{ status?: number; message: string } | null>(null);
  const [data, setData] = useState<CompanyDataResponse | null>(null);
  const [activeTab, setActiveTab] = useState<"profile" | "docs" | "team">(
    "profile",
  );
  // Onboarding Wizard Step (1: Company & Competitors, 2: Roles Definition, 3: Company Procedures & Drive)
  const [setupStep, setSetupStep] = useState<1 | 2 | 3>(1);
  // Setup Form State - Step 1: Profile & Competitors
  const [newName, setNewName] = useState("");
  const [newDomain, setNewDomain] = useState("");
  const [newSubdomain, setNewSubdomain] = useState("");
  const [subdomainTouched, setSubdomainTouched] = useState(false);
  const [newDesc, setNewDesc] = useState("");
  const [newProducts, setNewProducts] = useState("");
  const [newValueProp, setNewValueProp] = useState("");
  const [newAudience, setNewAudience] = useState("");
  const [newCompetitors, setNewCompetitors] = useState("");
  const [newKeywords, setNewKeywords] = useState("");
  // Setup Form State - Step 2: Roles Definition & My Role
  const [setupRoles, setSetupRoles] = useState<string[]>([
    ...DEFAULT_COMPANY_ROLES,
  ]);
  const [setupCustomRoleInput, setSetupCustomRoleInput] = useState("");
  const [myChosenRole, setMyChosenRole] = useState<string>(
    DEFAULT_COMPANY_ROLES[0] ?? "Sales / AE",
  );
  // Setup Form State - Step 3: Company Procedures & Drive
  const [setupProceduresText, setSetupProceduresText] = useState("");
  const [creating, setCreating] = useState(false);
  // Edit form state (for existing company)
  const [newAllowedDomains, setNewAllowedDomains] = useState<string[]>([]);
  const [newDomainInput, setNewDomainInput] = useState("");
  const [editAllowedDomains, setEditAllowedDomains] = useState<string[]>([]);
  const [editDomainInput, setEditDomainInput] = useState("");

  function cleanDomainInput(d: string): string {
    return d.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/^@/, "").replace(/\/.*$/, "");
  }

  function handleAddNewAllowedDomain() {
    const clean = cleanDomainInput(newDomainInput);
    if (!clean) return;
    if (newAllowedDomains.includes(clean)) {
      toast.push({ tone: "error", message: "Domain already added." });
      return;
    }
    setNewAllowedDomains([...newAllowedDomains, clean]);
    setNewDomainInput("");
  }

  function handleRemoveNewAllowedDomain(d: string) {
    setNewAllowedDomains(newAllowedDomains.filter((x) => x !== d));
  }

  function handleAddEditAllowedDomain() {
    const clean = cleanDomainInput(editDomainInput);
    if (!clean) return;
    if (editAllowedDomains.includes(clean)) {
      toast.push({ tone: "error", message: "Domain already added." });
      return;
    }
    setEditAllowedDomains([...editAllowedDomains, clean]);
    setEditDomainInput("");
  }

  function handleRemoveEditAllowedDomain(d: string) {
    setEditAllowedDomains(editAllowedDomains.filter((x) => x !== d));
  }
  const [editName, setEditName] = useState("");
  const [editDomain, setEditDomain] = useState("");
  const [editSubdomain, setEditSubdomain] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editProducts, setEditProducts] = useState("");
  const [editValueProp, setEditValueProp] = useState("");
  const [editAudience, setEditAudience] = useState("");
  const [editCompetitors, setEditCompetitors] = useState("");
  const [editKeywords, setEditKeywords] = useState("");
  const [editRoles, setEditRoles] = useState<string[]>([
    ...DEFAULT_COMPANY_ROLES,
  ]);
  const [customRoleInput, setCustomRoleInput] = useState("");
  const [editProceduresText, setEditProceduresText] = useState("");
  const [myCurrentRole, setMyCurrentRole] = useState("");
  const [saving, setSaving] = useState(false);
  const [savingProcedures, setSavingProcedures] = useState(false);
  const [savingRole, setSavingRole] = useState(false);
  // Document upload state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadCategory, setUploadCategory] = useState<
    "procedure" | "knowledge"
  >("procedure");
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docLinkUrl, setDocLinkUrl] = useState("");
  const [linkCategory, setLinkCategory] = useState<"procedure" | "knowledge">(
    "procedure",
  );
  const [addingLink, setAddingLink] = useState(false);
  // Monday.com connector state
  const [mondayModalOpen, setMondayModalOpen] = useState(false);
  const [mondayApiKey, setMondayApiKey] = useState("");
  const [mondayTesting, setMondayTesting] = useState(false);
  const [mondayConnectedUser, setMondayConnectedUser] = useState<string | null>(
    null,
  );
  const [mondayDocs, setMondayDocs] = useState<
    Array<{ id: string; title: string; docKind?: string }>
  >([]);
  const [mondayBoards, setMondayBoards] = useState<
    Array<{ id: string; name: string; type: string; description?: string }>
  >([]);
  const [mondaySearch, setMondaySearch] = useState("");
  const [mondayFilterType, setMondayFilterType] = useState<"all" | "boards" | "docs">("all");

  const filteredMondayDocs = useMemo(() => {
    if (mondayFilterType === "boards") return [];
    const q = mondaySearch.trim().toLowerCase();
    if (!q) return mondayDocs;
    return mondayDocs.filter(
      (d) =>
        (d.title || "").toLowerCase().includes(q) ||
        (d.id || "").toLowerCase().includes(q)
    );
  }, [mondayDocs, mondaySearch, mondayFilterType]);

  const filteredMondayBoards = useMemo(() => {
    if (mondayFilterType === "docs") return [];
    // Ignore any subitem boards ("sub_items_board", "subitems of...")
    const cleanBoards = mondayBoards.filter((b) => {
      const type = (b.type || "").toLowerCase();
      const name = (b.name || "").toLowerCase();
      return (
        type !== "sub_items_board" &&
        !type.includes("subitem") &&
        !name.startsWith("subitems of") &&
        !name.includes("subitem")
      );
    });
    const q = mondaySearch.trim().toLowerCase();
    if (!q) return cleanBoards;
    return cleanBoards.filter(
      (b) =>
        (b.name || "").toLowerCase().includes(q) ||
        (b.description || "").toLowerCase().includes(q) ||
        (b.id || "").toLowerCase().includes(q)
    );
  }, [mondayBoards, mondaySearch, mondayFilterType]);
  const [loadingMondayItems, setLoadingMondayItems] = useState(false);
  const [mondayDirectUrl, setMondayDirectUrl] = useState("");
  const [mondayCategory, setMondayCategory] = useState<
    "procedure" | "knowledge"
  >("procedure");
  const [importingMonday, setImportingMonday] = useState(false);
  // SharePoint / OneDrive connector state
  const [sharepointModalOpen, setSharepointModalOpen] = useState(false);
  const [sharepointUrl, setSharepointUrl] = useState("");
  const [sharepointToken, setSharepointToken] = useState("");
  const [sharepointCategory, setSharepointCategory] = useState<
    "procedure" | "knowledge"
  >("procedure");
  const [importingSharepoint, setImportingSharepoint] = useState(false);

  // Member invite & management state
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteRole, setInviteRole] = useState(DEFAULT_COMPANY_ROLES[0] as string);
  const [inviteManagerUid, setInviteManagerUid] = useState("");
  const [inviteCompanyRole, setInviteCompanyRole] = useState<"admin" | "member">("member");
  const [inviteConnectors, setInviteConnectors] = useState<ConnectorType[]>([
    "uploads",
    "googleDrive",
    "monday",
    "sharepoint",
  ]);
  const [invitingMember, setInvitingMember] = useState(false);
  const [inviteResult, setInviteResult] = useState<{
    email: string;
    inviteUrl: string;
    emailDispatched: boolean;
    copied?: boolean;
  } | null>(null);

  const [editMemberModalOpen, setEditMemberModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [editMemberRole, setEditMemberRole] = useState<string>("");
  const [editMemberManagerUid, setEditMemberManagerUid] = useState<string>("");
  const [editMemberCompanyRole, setEditMemberCompanyRole] = useState<"admin" | "member">("member");
  const [editMemberConnectors, setEditMemberConnectors] = useState<ConnectorType[]>([]);
  const [savingMember, setSavingMember] = useState(false);

  const [overviewModalOpen, setOverviewModalOpen] = useState(false);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewMember, setOverviewMember] = useState<TeamMember | null>(null);
  const [overviewData, setOverviewData] = useState<MemberOverviewResponse | null>(null);
  async function handleInviteMember() {
    if (!inviteEmail.trim() || !inviteEmail.includes("@")) {
      toast.push({ tone: "error", message: "Valid email is required" });
      return;
    }
    setInvitingMember(true);
    try {
      const res = await companyApi.inviteMember({
        email: inviteEmail.trim(),
        name: inviteName.trim() || undefined,
        companyRole: inviteCompanyRole,
        functionalRole: inviteRole,
        managerUid: inviteManagerUid || undefined,
        allowedConnectors: inviteConnectors,
      });
      const subdomain = data?.organization?.subdomain || data?.organization?.id || "";
      const inviteUrl = res.inviteUrl || `https://${subdomain}.sellsynq.com/signin?invited=1`;
      setInviteResult({
        email: inviteEmail.trim(),
        inviteUrl,
        emailDispatched: Boolean(res.emailDispatched),
      });
      toast.push({ tone: "success", message: `${inviteEmail.trim()} is pre-approved & invited!` });
      await loadData();
    } catch (e: any) {
      toast.push({ tone: "error", message: e?.message || "Failed to invite member" });
    } finally {
      setInvitingMember(false);
    }
  }

  function handleOpenEditMember(m: TeamMember) {
    setEditingMember(m);
    setEditMemberRole(m.functionalRole || m.role || DEFAULT_COMPANY_ROLES[0] || "");
    setEditMemberManagerUid(m.managerUid || "");
    setEditMemberCompanyRole((m.companyRole as "admin" | "member") || "member");
    setEditMemberConnectors(
      m.allowedConnectors && m.allowedConnectors.length > 0
        ? m.allowedConnectors
        : ["uploads", "googleDrive", "monday", "sharepoint"],
    );
    setEditMemberModalOpen(true);
  }

  async function handleSaveMember() {
    if (!editingMember) return;
    setSavingMember(true);
    try {
      await companyApi.updateMember(editingMember.uid, {
        functionalRole: editMemberRole,
        managerUid: editMemberManagerUid || null,
        companyRole: editMemberCompanyRole,
        allowedConnectors: editMemberConnectors,
      });
      toast.push({ tone: "success", message: "Member permissions updated" });
      setEditMemberModalOpen(false);
      await loadData();
    } catch (e: any) {
      toast.push({ tone: "error", message: e?.message || "Failed to update member" });
    } finally {
      setSavingMember(false);
    }
  }

  async function handleRemoveMember(m: TeamMember) {
    if (!confirm(`Are you sure you want to remove ${m.name || m.email} from the organization?`)) {
      return;
    }
    try {
      await companyApi.removeMember(m.uid);
      toast.push({ tone: "success", message: "Member removed" });
      await loadData();
    } catch (e: any) {
      toast.push({ tone: "error", message: e?.message || "Failed to remove member" });
    }
  }

  async function handleOpenOverview(m: TeamMember) {
    setOverviewMember(m);
    setOverviewModalOpen(true);
    setOverviewLoading(true);
    setOverviewData(null);
    try {
      const res = await companyApi.getMemberOverview(m.uid);
      setOverviewData(res);
    } catch (e: any) {
      toast.push({ tone: "error", message: e?.message || "Failed to load employee overview" });
    } finally {
      setOverviewLoading(false);
    }
  }

  async function handleToggleOverviewTask(task: TaskView) {
    if (!overviewData) return;
    const newDone = !task.done;
    setOverviewData({
      ...overviewData,
      tasks: overviewData.tasks.map((t) =>
        t.taskId === task.taskId ? { ...t, done: newDone } : t,
      ),
      stats: {
        ...overviewData.stats,
        openTasksCount: overviewData.stats.openTasksCount + (newDone ? -1 : 1),
        completedTasksCount: overviewData.stats.completedTasksCount + (newDone ? 1 : -1),
      },
    });
    try {
      await tasksApi.updateTask(task.taskId, { done: newDone });
    } catch {
      setOverviewData(overviewData);
    }
  }

  async function loadData() {
    try {
      setLoading(true);
      setLoadError(null);
      const res = await companyApi.getMyOrganization();
      setData(
        res || {
          organization: null,
          role: null,
          isSuperAdmin: false,
          documents: [],
          members: [],
        },
      );
      if (res?.organization) {
        setEditName(res.organization.name || "");
        setEditDomain(res.organization.domain || "");
        setEditSubdomain(
          res.organization.subdomain || res.organization.id || "",
        );
        setEditDesc(res.organization.description || "");
        setEditProducts(res.organization.productsServices || "");
        setEditValueProp(res.organization.valueProposition || "");
        setEditAudience(res.organization.targetAudience || "");
        setEditCompetitors(res.organization.competitors || "");
        setEditKeywords(res.organization.pitchKeywords || "");
        const loadedRoles =
          Array.isArray(res.organization.roles) &&
          res.organization.roles.length > 0
            ? Array.from(new Set(res.organization.roles.map(cleanRoleName).filter(Boolean)))
            : [...DEFAULT_COMPANY_ROLES];
        setEditRoles(loadedRoles);
        setEditProceduresText(res.organization.proceduresText || "");
        const assignedRole = cleanRoleName(
          res.functionalRole ||
          (user as any)?.functionalRole ||
          res.role ||
          "Sales / Account Executive (AE)",
        );
        setMyCurrentRole(assignedRole);
        if (res.organization.integrations?.monday) {
          const m = res.organization.integrations.monday;
          if (m.connected) {
            setMondayConnectedUser(m.accountName || m.userEmail || "Connected");
          }
          if (m.apiKey) {
            setMondayApiKey(m.apiKey);
            fetchMondayItems(m.apiKey);
          }
        }
        const loadedAllowed = Array.isArray(res.organization.allowedDomains) && res.organization.allowedDomains.length > 0
          ? res.organization.allowedDomains
          : res.organization.domain ? [res.organization.domain] : [];
        setEditAllowedDomains(loadedAllowed);
      } else {
        const tenantSub = getTenantSubdomain();
        if (tenantSub) {
          setNewSubdomain(tenantSub);
          const defaultDomain = `${tenantSub}.com`;
          setNewDomain(defaultDomain);
          setNewAllowedDomains([defaultDomain]);
          setNewName(tenantSub.charAt(0).toUpperCase() + tenantSub.slice(1));
        } else if (user?.companyName) {
          setNewName(user.companyName);
          const domain = user.email?.split("@")[1];
          if (domain && !["gmail.com", "google.com", "yahoo.com", "outlook.com", "hotmail.com"].includes(domain)) {
            setNewDomain(domain);
            setNewAllowedDomains([domain]);
            const sub = domain.split(".")[0];
            if (sub) setNewSubdomain(sub);
          }
        }
      }
    } catch (err: any) {
      console.error("Failed to load company:", err);
      const status = err?.status || err?.statusCode;
      const message = err?.message || "Failed to load company workspace details";
      setLoadError({ status, message });
      setData({
        organization: null,
        role: null,
        isSuperAdmin: false,
        documents: [],
        members: [],
      });
      const tenantSub = getTenantSubdomain();
      if (tenantSub && status !== 403) {
        setNewSubdomain(tenantSub);
        const defaultDomain = `${tenantSub}.com`;
        setNewDomain(defaultDomain);
        setNewAllowedDomains([defaultDomain]);
        setNewName(tenantSub.charAt(0).toUpperCase() + tenantSub.slice(1));
      } else if (user?.companyName && status !== 403) {
        setNewName(user.companyName);
      }
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    loadData();
  }, []);
  const isAdmin = data?.isSuperAdmin || data?.role === "admin";
  // Setup Step 2 Role helpers
  function handleAddSetupRole() {
    const trimmed = setupCustomRoleInput.trim();
    if (!trimmed) return;
    if (setupRoles.includes(trimmed)) {
      toast.push({ tone: "error", message: "Role already exists." });
      return;
    }
    setSetupRoles([...setupRoles, trimmed]);
    setSetupCustomRoleInput("");
  }
  function handleRemoveSetupRole(roleToRemove: string) {
    if (setupRoles.length <= 1) {
      toast.push({ tone: "error", message: "At least one role is required." });
      return;
    }
    const updated = setupRoles.filter((r) => r !== roleToRemove);
    setSetupRoles(updated);
    if (myChosenRole === roleToRemove) {
      setMyChosenRole(updated[0] ?? "");
    }
  }
  // Final Onboarding Submit
  async function handleFinishOnboarding() {
    if (!newName.trim()) {
      setSetupStep(1);
      toast.push({ tone: "error", message: "Company name is required." });
      return;
    }
    setCreating(true);
    try {
      const res = await companyApi.createOrganization({
        name: newName.trim(),
        domain: newDomain.trim(),
        subdomain: newSubdomain.trim() || undefined,
        allowedDomains: newAllowedDomains.length > 0 ? newAllowedDomains : (newDomain.trim() ? [newDomain.trim()] : undefined),
        description: newDesc.trim(),
        productsServices: newProducts.trim(),
        valueProposition: newValueProp.trim(),
        targetAudience: newAudience.trim(),
        competitors: newCompetitors.trim(),
        pitchKeywords: newKeywords.trim(),
        roles: setupRoles,
        proceduresText: setupProceduresText.trim(),
        myRole: myChosenRole,
        mondayApiKey: mondayApiKey.trim() || undefined,
      });
      toast.push({
        tone: "success",
        message: `Workspace ready at https://${res.organization.subdomain || res.organization.id}.sellsynq.com`,
      });
      if (user) {
        setUser({
          ...user,
          companyId: res.organization.id,
          companyName: res.organization.name,
          companyRole: "admin",
          role: "admin",
          functionalRole: myChosenRole,
        });
      }
      const targetSub = (res.organization.subdomain || res.organization.id || "").toLowerCase();
      const currentSub = getTenantSubdomain();
      if (targetSub && currentSub !== targetSub) {
        if (typeof window !== "undefined") {
          if (window.location.hostname.includes("sellsynq.com")) {
            window.location.href = `https://${targetSub}.sellsynq.com/company`;
            return;
          } else if (window.location.hostname.includes("localhost")) {
            window.location.href = `${window.location.protocol}//${targetSub}.${window.location.host}/company`;
            return;
          }
        }
      }
      await loadData();
    } catch (e) {
      toast.push({
        tone: "error",
        message: `Creation failed: ${(e as Error).message}`,
      });
    } finally {
      setCreating(false);
    }
  }
  // Save Company Profile (Existing company)
  async function handleSaveProfile(e: React.FormEvent) {
    e.preventDefault();
    if (!isAdmin) return;
    setSaving(true);
    try {
      const res = await companyApi.updateOrganization({
        name: editName.trim(),
        domain: editDomain.trim(),
        subdomain: editSubdomain.trim() || undefined,
        allowedDomains: editAllowedDomains,
        description: editDesc.trim(),
        productsServices: editProducts.trim(),
        valueProposition: editValueProp.trim(),
        targetAudience: editAudience.trim(),
        competitors: editCompetitors.trim(),
        pitchKeywords: editKeywords.trim(),
        roles: editRoles,
      });
      toast.push({ tone: "success", message: "Company profile updated." });
      setData((prev) =>
        prev ? { ...prev, organization: res.organization } : prev,
      );
      if (user) {
        setUser({ ...user, companyName: res.organization.name });
      }
    } catch (e) {
      toast.push({
        tone: "error",
        message: `Save failed: ${(e as Error).message}`,
      });
    } finally {
      setSaving(false);
    }
  }
  // Save Procedures (Existing company)
  async function handleSaveProcedures() {
    if (!isAdmin) return;
    setSavingProcedures(true);
    try {
      const res = await companyApi.updateOrganization({
        proceduresText: editProceduresText.trim(),
      });
      toast.push({
        tone: "success",
        message: "Live procedures & guidelines updated!",
      });
      setData((prev) =>
        prev ? { ...prev, organization: res.organization } : prev,
      );
    } catch (e) {
      toast.push({
        tone: "error",
        message: `Failed to save procedures: ${(e as Error).message}`,
      });
    } finally {
      setSavingProcedures(false);
    }
  }
  // Update My Role (Any user)
  async function handleUpdateMyRole(role: string) {
    setSavingRole(true);
    try {
      await companyApi.updateMyRole(role);
      setMyCurrentRole(role);
      toast.push({
        tone: "success",
        message: `Your active role is now: ${role}`,
      });
      if (user) {
        setUser({ ...user, functionalRole: role });
      }
    } catch (e) {
      toast.push({
        tone: "error",
        message: `Failed to change role: ${(e as Error).message}`,
      });
    } finally {
      setSavingRole(false);
    }
  }
  // Add role to existing company
  function handleAddExistingRole() {
    const trimmed = customRoleInput.trim();
    if (!trimmed) return;
    if (editRoles.includes(trimmed)) {
      toast.push({ tone: "error", message: "Role already exists." });
      return;
    }
    const updated = [...editRoles, trimmed];
    setEditRoles(updated);
    setCustomRoleInput("");
  }
  function handleRemoveExistingRole(r: string) {
    if (editRoles.length <= 1) {
      toast.push({ tone: "error", message: "At least one role is required." });
      return;
    }
    const updated = editRoles.filter((item) => item !== r);
    setEditRoles(updated);
  }
  // Document Upload
  async function handleFileUpload(files: FileList | null) {
    if (!files || files.length === 0) return;
    setUploadingDoc(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i]!;
        await companyApi.uploadDocument(file, uploadCategory);
      }
      toast.push({
        tone: "success",
        message: `${uploadCategory === "procedure" ? "Company Procedure" : "Document"} uploaded and indexed into Live Grounding.`,
      });
      await loadData();
    } catch (e) {
      toast.push({
        tone: "error",
        message: `Upload failed: ${(e as Error).message}`,
      });
    } finally {
      setUploadingDoc(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }
  // Add Link
  async function handleAddLink() {
    const trimmed = docLinkUrl.trim();
    if (!trimmed) return;
    if (trimmed.includes("monday.com")) {
      const hasKey = Boolean(
        mondayApiKey || data?.organization?.integrations?.monday?.apiKey,
      );
      if (!hasKey) {
        setMondayDirectUrl(trimmed);
        setMondayModalOpen(true);
        toast.push({
          tone: "info",
          message:
            "Please connect your Monday API token to import this document.",
        });
        return;
      }
    }
    setAddingLink(true);
    try {
      await companyApi.addDocumentLink(
        docLinkUrl.trim(),
        googleToken ?? undefined,
        linkCategory,
      );
      toast.push({
        tone: "success",
        message: "Document link indexed into Live Grounding.",
      });
      setDocLinkUrl("");
      await loadData();
    } catch (e) {
      toast.push({
        tone: "error",
        message: `Failed to add link: ${(e as Error).message}`,
      });
    } finally {
      setAddingLink(false);
    }
  }
  // Monday connector functions
  async function fetchMondayItems(key?: string) {
    setLoadingMondayItems(true);
    try {
      const k = (
        key ||
        mondayApiKey ||
        data?.organization?.integrations?.monday?.apiKey ||
        ""
      ).trim();
      const [docsRes, boardsRes] = await Promise.allSettled([
        companyApi.getMondayDocs(k || undefined),
        companyApi.getMondayBoards(k || undefined),
      ]);
      if (docsRes.status === "fulfilled") {
        setMondayDocs(docsRes.value.docs || []);
      }
      if (boardsRes.status === "fulfilled") {
        setMondayBoards(boardsRes.value.boards || []);
      }
    } catch {
      // quiet fail
    } finally {
      setLoadingMondayItems(false);
    }
  }
  async function handleTestMonday(keyToTest?: string) {
    const key = (keyToTest || mondayApiKey).trim();
    if (!key) {
      toast.push({
        tone: "error",
        message: "Please enter your Monday Personal API Token.",
      });
      return;
    }
    setMondayTesting(true);
    try {
      const res = await companyApi.testMondayConnection(key);
      if (res.ok) {
        const who = res.user.name || res.user.email;
        const acct = res.user.accountName ? ` - ${res.user.accountName}` : "";
        setMondayConnectedUser(`${who}${acct}`);
        toast.push({
          tone: "success",
          message: `Connected to Monday.com as ${who}!`,
        });
        await fetchMondayItems(key);
        await loadData();
      }
    } catch (err) {
      toast.push({
        tone: "error",
        message: `Monday connection failed: ${(err as Error).message}`,
      });
    } finally {
      setMondayTesting(false);
    }
  }
  async function handleImportMonday(
    type: "doc" | "board" | "url",
    idOrUrl?: string,
  ) {
    setImportingMonday(true);
    try {
      const k = mondayApiKey.trim() || undefined;
      if (type === "url") {
        const url = (idOrUrl || mondayDirectUrl).trim();
        if (!url) {
          toast.push({
            tone: "error",
            message: "Please paste a Monday doc or board URL.",
          });
          return;
        }
        await companyApi.importMondayDocument({
          type: "url",
          url,
          category: mondayCategory,
          apiKey: k,
        });
        toast.push({
          tone: "success",
          message: "Monday document imported and indexed into Live Grounding!",
        });
        setMondayDirectUrl("");
      } else if (type === "doc") {
        await companyApi.importMondayDocument({
          type: "doc",
          id: idOrUrl,
          category: mondayCategory,
          apiKey: k,
        });
        toast.push({
          tone: "success",
          message: "Monday Workdoc imported into Live Grounding!",
        });
      } else if (type === "board") {
        await companyApi.importMondayDocument({
          type: "board",
          id: idOrUrl,
          category: mondayCategory,
          apiKey: k,
        });
        toast.push({
          tone: "success",
          message: "Monday Board imported into Live Grounding!",
        });
      }
      await loadData();
      setMondayModalOpen(false);
    } catch (err) {
      toast.push({
        tone: "error",
        message: `Import failed: ${(err as Error).message}`,
      });
    } finally {
      setImportingMonday(false);
    }
  }
  // SharePoint connector functions
  async function handleImportSharePoint() {
    const url = sharepointUrl.trim();
    if (!url) {
      toast.push({
        tone: "error",
        message: "Please paste a SharePoint or OneDrive document link.",
      });
      return;
    }
    setImportingSharepoint(true);
    try {
      await companyApi.importSharePointDocument({
        url,
        token: sharepointToken.trim() || undefined,
        category: sharepointCategory,
      });
      toast.push({
        tone: "success",
        message:
          "SharePoint document imported and indexed into Live Grounding!",
      });
      setSharepointUrl("");
      setSharepointToken("");
      setSharepointModalOpen(false);
      await loadData();
    } catch (err) {
      toast.push({
        tone: "error",
        message: `SharePoint import failed: ${(err as Error).message}`,
      });
    } finally {
      setImportingSharepoint(false);
    }
  }
  async function handleDeleteDoc(id: string) {
    if (
      !window.confirm("Remove this document from the company knowledge base?")
    )
      return;
    try {
      await companyApi.deleteDocument(id);
      toast.push({ tone: "success", message: "Document removed." });
      setData((prev) =>
        prev
          ? { ...prev, documents: prev.documents.filter((d) => d.id !== id) }
          : prev,
      );
    } catch (e) {
      toast.push({
        tone: "error",
        message: `Delete failed: ${(e as Error).message}`,
      });
    }
  }
  async function handleConnectDrive() {
    try {
      const tok = await connectGoogleDrive();
      if (tok) {
        setGoogleToken(tok);
        toast.push({ tone: "success", message: "Connected Google Drive." });
      }
    } catch (e) {
      toast.push({
        tone: "error",
        message: `Drive connection failed: ${(e as Error).message}`,
      });
    }
  }
  const currentUserMember = data?.members?.find(
    (m) => m.uid === user?.uid || m.email?.toLowerCase() === user?.email?.toLowerCase(),
  );
  const isUserAdmin = Boolean(data?.role === "admin" || data?.isSuperAdmin);
  const userAllowedConnectors: ConnectorType[] = isUserAdmin
    ? ["uploads", "googleDrive", "monday", "sharepoint"]
    : (currentUserMember?.allowedConnectors && currentUserMember.allowedConnectors.length > 0
        ? currentUserMember.allowedConnectors
        : ["uploads", "googleDrive", "monday", "sharepoint"]);

  const displayedDocs = !data?.documents ? [] : (isUserAdmin
    ? data.documents
    : data.documents.filter((doc) => {
        const src = doc.source || "upload";
        if (src === "upload" || src === "web") return userAllowedConnectors.includes("uploads");
        if (src === "gdrive") return userAllowedConnectors.includes("googleDrive");
        if (src === "monday") return userAllowedConnectors.includes("monday");
        if (src === "sharepoint") return userAllowedConnectors.includes("sharepoint");
        return true;
      }));

  return (
    <div className="dash">
      <DashHeader />
      <main className="dash-main">
        <ErrorBoundary>
          {loading ? (
            <div
              style={{
                textAlign: "center",
                padding: 60,
                color: "var(--text-3)",
              }}
            >
              Loading company details…
            </div>
          ) : loadError ? (
            <div
              style={{
                maxWidth: 600,
                margin: "40px auto",
                padding: 32,
                background: "var(--surface)",
                border: "1px solid var(--border-soft)",
                borderRadius: 16,
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: 32, marginBottom: 12 }}>{loadError.status === 403 ? "🔒" : "⚠️"}</div>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: "var(--text-1)", marginBottom: 8 }}>
                {loadError.status === 403 ? "Workspace Access Restricted" : "Could Not Load Workspace"}
              </h2>
              <p style={{ fontSize: 14, color: "var(--text-2)", lineHeight: 1.6, marginBottom: 20 }}>
                {loadError.message}
              </p>
              <Button variant="primary" onClick={() => loadData()}>
                Retry
              </Button>
            </div>
          ) : !data?.organization ? (
            // ================= STATE 1: ONBOARDING / CREATE COMPANY WIZARD =================
            <div style={{ maxWidth: 800, margin: "0 auto", paddingBottom: 60 }}>
              {/* Header */}
              <div style={{ marginBottom: 28, textAlign: "center" }}>
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 54,
                    height: 54,
                    borderRadius: 14,
                    background: "rgba(99, 102, 241, 0.12)",
                    color: "var(--primary)",
                    marginBottom: 14,
                  }}
                >
                  <Globe size={26} />
                </div>
                <h1
                  style={{
                    fontSize: 26,
                    fontWeight: 700,
                    color: "var(--text-1)",
                    marginBottom: 8,
                  }}
                >
                  Set Up Your Company & Knowledge Grounding
                </h1>
                <p
                  style={{
                    fontSize: 14,
                    color: "var(--text-2)",
                    maxWidth: 620,
                    margin: "0 auto",
                    lineHeight: 1.5,
                  }}
                >
                  Configure your company profile, define user roles, and connect
                  company procedures so SellSynq's AI Copilot gives instant,
                  role-tailored answers in live meetings.
                </p>
              </div>
              {/* Progress Stepper */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: 12,
                  marginBottom: 24,
                }}
              >
                <div
                  onClick={() => setSetupStep(1)}
                  style={{
                    cursor: "pointer",
                    padding: "12px 14px",
                    borderRadius: 10,
                    background:
                      setupStep === 1
                        ? "rgba(99, 102, 241, 0.12)"
                        : "var(--surface)",
                    border:
                      setupStep === 1
                        ? "1px solid var(--primary)"
                        : "1px solid var(--border-soft)",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <span
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      background:
                        setupStep === 1
                          ? "var(--primary)"
                          : "var(--surface-hover)",
                      color: setupStep === 1 ? "#fff" : "var(--text-2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    1
                  </span>
                  <div>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color:
                          setupStep === 1 ? "var(--primary)" : "var(--text-1)",
                      }}
                    >
                      Company &amp; Competitors
                    </div>
                    <div style={{ fontSize: 11, color: "var(--text-3)" }}>
                      Offerings &amp; Market
                    </div>
                  </div>
                </div>
                <div
                  onClick={() => {
                    if (newName.trim()) setSetupStep(2);
                  }}
                  style={{
                    cursor: newName.trim() ? "pointer" : "default",
                    padding: "12px 14px",
                    borderRadius: 10,
                    background:
                      setupStep === 2
                        ? "rgba(99, 102, 241, 0.12)"
                        : "var(--surface)",
                    border:
                      setupStep === 2
                        ? "1px solid var(--primary)"
                        : "1px solid var(--border-soft)",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    opacity: newName.trim() ? 1 : 0.6,
                  }}
                >
                  <span
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      background:
                        setupStep === 2
                          ? "var(--primary)"
                          : "var(--surface-hover)",
                      color: setupStep === 2 ? "#fff" : "var(--text-2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    2
                  </span>
                  <div>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color:
                          setupStep === 2 ? "var(--primary)" : "var(--text-1)",
                      }}
                    >
                      Roles Definition
                    </div>
                    <div style={{ fontSize: 11, color: "var(--text-3)" }}>
                      Sales, Support, Pre-Sale
                    </div>
                  </div>
                </div>
                <div
                  onClick={() => {
                    if (newName.trim()) setSetupStep(3);
                  }}
                  style={{
                    cursor: newName.trim() ? "pointer" : "default",
                    padding: "12px 14px",
                    borderRadius: 10,
                    background:
                      setupStep === 3
                        ? "rgba(99, 102, 241, 0.12)"
                        : "var(--surface)",
                    border:
                      setupStep === 3
                        ? "1px solid var(--primary)"
                        : "1px solid var(--border-soft)",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    opacity: newName.trim() ? 1 : 0.6,
                  }}
                >
                  <span
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      background:
                        setupStep === 3
                          ? "var(--primary)"
                          : "var(--surface-hover)",
                      color: setupStep === 3 ? "#fff" : "var(--text-2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    3
                  </span>
                  <div>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color:
                          setupStep === 3 ? "var(--primary)" : "var(--text-1)",
                      }}
                    >
                      Procedures &amp; Drive
                    </div>
                    <div style={{ fontSize: 11, color: "var(--text-3)" }}>
                      Live Copilot Grounding
                    </div>
                  </div>
                </div>
              </div>
              {/* STEP 1: Profile & Offerings & Competitors */}
              {setupStep === 1 && (
                <div
                  style={{
                    background: "var(--surface)",
                    border: "1px solid var(--border-soft)",
                    borderRadius: 16,
                    padding: 28,
                    display: "flex",
                    flexDirection: "column",
                    gap: 18,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 16,
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          fontSize: 13,
                          fontWeight: 600,
                          color: "var(--text-1)",
                          marginBottom: 6,
                        }}
                      >
                        Company Name *
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="e.g. Acme Tech Solutions"
                        value={newName}
                        onChange={(e) => {
                          const val = e.target.value;
                          setNewName(val);
                          if (!subdomainTouched) {
                            const slug = slugifySubdomain(val);
                            if (slug) {
                              setNewSubdomain(slug);
                            } else if (newDomain) {
                              const domSlug = newDomain.split(".")[0]?.toLowerCase().replace(/[^a-z0-9-]/g, "");
                              if (domSlug && !["gmail", "google", "outlook", "yahoo"].includes(domSlug)) {
                                setNewSubdomain(domSlug);
                              }
                            }
                          }
                        }}
                        style={{
                          width: "100%",
                          padding: "10px 14px",
                          borderRadius: 8,
                          border: "1px solid var(--border-soft)",
                          background: "var(--surface-hover)",
                          color: "var(--text-1)",
                          fontSize: 14,
                        }}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          fontSize: 13,
                          fontWeight: 600,
                          color: "var(--text-1)",
                          marginBottom: 6,
                        }}
                      >
                        Company Domain / Website
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. acme.com"
                        value={newDomain}
                        onChange={(e) => {
                          const val = e.target.value;
                          setNewDomain(val);
                          if (!subdomainTouched && !newSubdomain) {
                            const sub = val.split(".")[0]?.toLowerCase().replace(/[^a-z0-9-]/g, "");
                            if (sub && !["gmail", "google", "outlook", "yahoo", "hotmail"].includes(sub)) {
                              setNewSubdomain(sub);
                            }
                          }
                        }}
                        style={{
                          width: "100%",
                          padding: "10px 14px",
                          borderRadius: 8,
                          border: "1px solid var(--border-soft)",
                          background: "var(--surface-hover)",
                          color: "var(--text-1)",
                          fontSize: 14,
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: 13,
                        fontWeight: 600,
                        color: "var(--text-1)",
                        marginBottom: 6,
                      }}
                    >
                      Company Workspace Subdomain (.sellsynq.com)
                    </label>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        position: "relative",
                      }}
                    >
                      <input
                        type="text"
                        placeholder="e.g. acme or wideops"
                        value={newSubdomain}
                        onChange={(e) => {
                          setSubdomainTouched(true);
                          setNewSubdomain(
                            e.target.value
                              .toLowerCase()
                              .replace(/[^a-z0-9-]/g, ""),
                          );
                        }}
                        style={{
                          width: "100%",
                          padding: "10px 14px",
                          paddingRight: 140,
                          borderRadius: 8,
                          border: "1px solid var(--border-soft)",
                          background: "var(--surface-hover)",
                          color: "var(--text-1)",
                          fontSize: 14,
                          fontFamily: "var(--font-mono, monospace)",
                        }}
                      />
                      <span
                        style={{
                          position: "absolute",
                          right: 12,
                          color: "var(--text-3)",
                          fontSize: 13,
                          pointerEvents: "none",
                          fontFamily: "var(--font-mono, monospace)",
                        }}
                      >
                        .sellsynq.com
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: 11.5,
                        color: "var(--text-3)",
                        marginTop: 4,
                        marginBottom: 16,
                      }}
                    >
                      Your team workspace login:{" "}
                      <strong style={{ color: "var(--primary)" }}>
                        https://{newSubdomain || "yourcompany"}.sellsynq.com
                      </strong>
                    </div>
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: 13,
                        fontWeight: 600,
                        color: "var(--text-1)",
                        marginBottom: 4,
                      }}
                    >
                      Allowed Email Domains &amp; Websites
                    </label>
                    <p style={{ fontSize: 12, color: "var(--text-3)", marginBottom: 8, marginTop: 0 }}>
                      Only users with an email address matching these domains will be permitted to access this workspace (e.g. <code>wideops.com</code>, <code>wideops.co.il</code>).
                    </p>
                    <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                      <input
                        type="text"
                        placeholder="e.g. wideops.co.il"
                        value={newDomainInput}
                        onChange={(e) => setNewDomainInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleAddNewAllowedDomain();
                          }
                        }}
                        style={{
                          flex: 1,
                          padding: "8px 12px",
                          borderRadius: 8,
                          border: "1px solid var(--border-soft)",
                          background: "var(--surface-hover)",
                          color: "var(--text-1)",
                          fontSize: 13,
                        }}
                      />
                      <button
                        type="button"
                        className="pill-btn secondary sm"
                        onClick={handleAddNewAllowedDomain}
                        style={{ whiteSpace: "nowrap", padding: "8px 14px", fontSize: 13 }}
                      >
                        + Add Domain
                      </button>
                    </div>
                    {newAllowedDomains.length > 0 && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
                        {newAllowedDomains.map((d) => (
                          <span
                            key={d}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 6,
                              padding: "4px 10px",
                              borderRadius: 16,
                              background: "var(--surface-hover)",
                              border: "1px solid var(--border-soft)",
                              fontSize: 12,
                              fontWeight: 500,
                              color: "var(--text-1)",
                            }}
                          >
                            @{d}
                            <button
                              type="button"
                              onClick={() => handleRemoveNewAllowedDomain(d)}
                              style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                color: "var(--text-3)",
                                padding: "0 2px",
                                fontSize: 14,
                                lineHeight: 1,
                              }}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: 13,
                        fontWeight: 600,
                        color: "var(--text-1)",
                        marginBottom: 6,
                      }}
                    >
                      Company Overview &amp; What You Do
                    </label>
                    <textarea
                      rows={2}
                      placeholder="Brief description of your business model, core focus, and industry."
                      value={newDesc}
                      onChange={(e) => setNewDesc(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "10px 14px",
                        borderRadius: 8,
                        border: "1px solid var(--border-soft)",
                        background: "var(--surface-hover)",
                        color: "var(--text-1)",
                        fontSize: 14,
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: 13,
                        fontWeight: 600,
                        color: "var(--text-1)",
                        marginBottom: 6,
                      }}
                    >
                      Products &amp; Solutions
                    </label>
                    <textarea
                      rows={2}
                      placeholder="List key product names, flagship tiers, features, or consulting services."
                      value={newProducts}
                      onChange={(e) => setNewProducts(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "10px 14px",
                        borderRadius: 8,
                        border: "1px solid var(--border-soft)",
                        background: "var(--surface-hover)",
                        color: "var(--text-1)",
                        fontSize: 14,
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: 13,
                        fontWeight: 600,
                        color: "var(--text-1)",
                        marginBottom: 6,
                      }}
                    >
                      Value Proposition &amp; Competitive Edge
                    </label>
                    <textarea
                      rows={2}
                      placeholder="Key differentiators, ROI advantages, proprietary tech, or speed to value."
                      value={newValueProp}
                      onChange={(e) => setNewValueProp(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "10px 14px",
                        borderRadius: 8,
                        border: "1px solid var(--border-soft)",
                        background: "var(--surface-hover)",
                        color: "var(--text-1)",
                        fontSize: 14,
                      }}
                    />
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 16,
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          fontSize: 13,
                          fontWeight: 600,
                          color: "var(--text-1)",
                          marginBottom: 6,
                        }}
                      >
                        Target Audience / ICP
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. CTOs, VP Sales, Mid-market B2B SaaS"
                        value={newAudience}
                        onChange={(e) => setNewAudience(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "10px 14px",
                          borderRadius: 8,
                          border: "1px solid var(--border-soft)",
                          background: "var(--surface-hover)",
                          color: "var(--text-1)",
                          fontSize: 14,
                        }}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          fontSize: 13,
                          fontWeight: 600,
                          color: "var(--text-1)",
                          marginBottom: 6,
                        }}
                      >
                        Known Competitors
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Salesforce, HubSpot, Gong"
                        value={newCompetitors}
                        onChange={(e) => setNewCompetitors(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "10px 14px",
                          borderRadius: 8,
                          border: "1px solid var(--border-soft)",
                          background: "var(--surface-hover)",
                          color: "var(--text-1)",
                          fontSize: 14,
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: 13,
                        fontWeight: 600,
                        color: "var(--text-1)",
                        marginBottom: 6,
                      }}
                    >
                      Key Pitch Keywords &amp; Buzzwords
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. AI-driven, Zero-friction, Enterprise Security, SOC2"
                      value={newKeywords}
                      onChange={(e) => setNewKeywords(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "10px 14px",
                        borderRadius: 8,
                        border: "1px solid var(--border-soft)",
                        background: "var(--surface-hover)",
                        color: "var(--text-1)",
                        fontSize: 14,
                      }}
                    />
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginTop: 8,
                    }}
                  >
                    <Button
                      type="button"
                      variant="primary"
                      onClick={() => {
                        if (!newName.trim()) {
                          toast.push({
                            tone: "error",
                            message: "Company name is required.",
                          });
                          return;
                        }
                        setSetupStep(2);
                      }}
                    >
                      Next: Define Company Roles →
                    </Button>
                  </div>
                </div>
              )}
              {/* STEP 2: Roles Definition & My Role */}
              {setupStep === 2 && (
                <div
                  style={{
                    background: "var(--surface)",
                    border: "1px solid var(--border-soft)",
                    borderRadius: 16,
                    padding: 28,
                    display: "flex",
                    flexDirection: "column",
                    gap: 20,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
                  }}
                >
                  <div>
                    <h3
                      style={{
                        fontSize: 18,
                        fontWeight: 700,
                        margin: "0 0 6px",
                        color: "var(--text-1)",
                      }}
                    >
                      Define Company User Roles
                    </h3>
                    <p
                      style={{
                        fontSize: 13,
                        color: "var(--text-2)",
                        margin: 0,
                        lineHeight: 1.5,
                      }}
                    >
                      Specify the roles your team members hold (e.g. Sales Rep,
                      Account Manager, BDR, Marketing, Support, Pre-Sales). SellSynq customizes its
                      live meeting hints, objection handling, and
                      recommendations specifically for each role!
                    </p>
                  </div>
                  {/* Configured Roles List */}
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: 13,
                        fontWeight: 600,
                        color: "var(--text-1)",
                        marginBottom: 8,
                      }}
                    >
                      Company Roles List ({setupRoles.length})
                    </label>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 8,
                        marginBottom: 14,
                      }}
                    >
                      {setupRoles.map((role) => (
                        <div
                          key={role}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "6px 12px",
                            borderRadius: 8,
                            background: "rgba(99, 102, 241, 0.08)",
                            border: "1px solid rgba(99, 102, 241, 0.25)",
                            fontSize: 13,
                            color: "var(--text-1)",
                          }}
                        >
                          <span>{role}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveSetupRole(role)}
                            style={{
                              background: "transparent",
                              border: "none",
                              color: "var(--text-3)",
                              cursor: "pointer",
                              padding: 2,
                              fontSize: 14,
                            }}
                            title="Remove role"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                    {/* Add Custom Role */}
                    <div style={{ display: "flex", gap: 8 }}>
                      <input
                        type="text"
                        placeholder="Add another role (e.g. Marketing, Account Manager, Sales Rep, BDR)…"
                        value={setupCustomRoleInput}
                        onChange={(e) =>
                          setSetupCustomRoleInput(e.target.value)
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleAddSetupRole();
                          }
                        }}
                        style={{
                          flex: 1,
                          padding: "9px 12px",
                          borderRadius: 8,
                          border: "1px solid var(--border-soft)",
                          background: "var(--surface-hover)",
                          color: "var(--text-1)",
                          fontSize: 13,
                        }}
                      />
                      <button
                        type="button"
                        className="pill-btn primary"
                        onClick={handleAddSetupRole}
                        disabled={!setupCustomRoleInput.trim()}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <Plus size={15} />
                        <span>Add Role</span>
                      </button>
                    </div>
                    {DEFAULT_COMPANY_ROLES.some((r) => !setupRoles.includes(r)) && (
                      <div
                        style={{
                          marginTop: 10,
                          display: "flex",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: 6,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 12,
                            color: "var(--text-3)",
                            fontWeight: 500,
                          }}
                        >
                          Quick Suggestions:
                        </span>
                        {DEFAULT_COMPANY_ROLES.filter((r) => !setupRoles.includes(r)).map((sug) => (
                          <button
                            key={sug}
                            type="button"
                            onClick={() => setSetupRoles([...setupRoles, sug])}
                            style={{
                              border: "1px dashed rgba(99, 102, 241, 0.4)",
                              background: "rgba(99, 102, 241, 0.05)",
                              color: "var(--brand-primary, #6366f1)",
                              borderRadius: 6,
                              padding: "4px 8px",
                              fontSize: 12,
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                          >
                            <Plus size={12} />
                            {sug}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Assign My Role */}
                  <div
                    style={{
                      background: "rgba(16, 185, 129, 0.06)",
                      border: "1px solid rgba(16, 185, 129, 0.2)",
                      borderRadius: 12,
                      padding: "16px 20px",
                    }}
                  >
                    <label
                      style={{
                        display: "block",
                        fontSize: 13,
                        fontWeight: 700,
                        color: "var(--text-1)",
                        marginBottom: 6,
                      }}
                    >
                      Your Active Role in the Company
                    </label>
                    <p
                      style={{
                        fontSize: 12,
                        color: "var(--text-2)",
                        marginBottom: 10,
                      }}
                    >
                      The AI coach will immediately tailor its live
                      recommendations, talk tracks, and tips to your selected
                      role.
                    </p>
                    <select
                      value={myChosenRole}
                      onChange={(e) => setMyChosenRole(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "10px 14px",
                        borderRadius: 8,
                        border: "1px solid var(--border-soft)",
                        background: "var(--surface)",
                        color: "var(--text-1)",
                        fontSize: 14,
                        fontWeight: 600,
                      }}
                    >
                      {setupRoles.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginTop: 8,
                    }}
                  >
                    <button
                      type="button"
                      className="ghost-btn"
                      onClick={() => setSetupStep(1)}
                    >
                      ← Back
                    </button>
                    <Button
                      type="button"
                      variant="primary"
                      onClick={() => setSetupStep(3)}
                    >
                      Next: Company Procedures &amp; Drive →
                    </Button>
                  </div>
                </div>
              )}
              {/* STEP 3: Company Procedures (SOPs) & Google Drive */}
              {setupStep === 3 && (
                <div
                  style={{
                    background: "var(--surface)",
                    border: "1px solid var(--border-soft)",
                    borderRadius: 16,
                    padding: 28,
                    display: "flex",
                    flexDirection: "column",
                    gap: 20,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
                  }}
                >
                  <div>
                    <h3
                      style={{
                        fontSize: 18,
                        fontWeight: 700,
                        margin: "0 0 6px",
                        color: "var(--text-1)",
                      }}
                    >
                      Company Procedures &amp; Knowledge Documents (SOPs)
                    </h3>
                    <p
                      style={{
                        fontSize: 13,
                        color: "var(--text-2)",
                        margin: 0,
                        lineHeight: 1.5,
                      }}
                    >
                      Connect Google Drive or outline company guidelines and
                      standard operating procedures (SOPs). During live customer
                      calls, the AI Copilot strictly follows these procedures to
                      answer questions accurately and uphold company standards.
                    </p>
                  </div>
                  {/* Procedures Text Input */}
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: 13,
                        fontWeight: 600,
                        color: "var(--text-1)",
                        marginBottom: 6,
                      }}
                    >
                      Company Procedures &amp; Policy Rules (Guidelines, Pricing
                      Limits, Discount Tiers, Cancellation Policies)
                    </label>
                    <textarea
                      rows={5}
                      placeholder={`Detail essential company rules the AI must enforce during live calls, e.g.:\n- Standard payment terms: Net 30. Max discount without VP approval: 15%.\n- Cancellation policy: 30-day notice with full data export support.\n- Support SLA: 1 hour response for enterprise tier, 4 hours for standard.\n- Escalation procedure: Critical outages escalate immediately to on-call engineering lead.`}
                      value={setupProceduresText}
                      onChange={(e) => setSetupProceduresText(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "12px 14px",
                        borderRadius: 8,
                        border: "1px solid var(--border-soft)",
                        background: "var(--surface-hover)",
                        color: "var(--text-1)",
                        fontSize: 13,
                        lineHeight: 1.5,
                      }}
                    />
                  </div>
                  {/* Google Drive Connect */}
                  <div
                    style={{
                      background: "rgba(99, 102, 241, 0.05)",
                      border: "1px solid rgba(99, 102, 241, 0.2)",
                      borderRadius: 12,
                      padding: "16px 20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 16,
                      flexWrap: "wrap",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: "var(--text-1)",
                          marginBottom: 2,
                        }}
                      >
                        Connect Google Drive Documents
                      </div>
                      <div style={{ fontSize: 12, color: "var(--text-3)" }}>
                        Link your team's Google Docs containing live procedure
                        guidelines and battlecards.
                      </div>
                    </div>
                    {googleToken ? (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          fontSize: 13,
                          fontWeight: 600,
                          color: "#10B981",
                        }}
                      >
                        <Check size={16} /> Google Drive Connected
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="pill-btn primary"
                        onClick={handleConnectDrive}
                        style={{ fontSize: 12 }}
                      >
                        Connect Google Drive
                      </button>
                    )}
                  </div>
                  {/* Monday.com Connect Card */}
                  <div
                    style={{
                      background: "rgba(226, 74, 104, 0.05)",
                      border: "1px solid rgba(226, 74, 104, 0.2)",
                      borderRadius: 12,
                      padding: "16px 20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 16,
                      flexWrap: "wrap",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: "var(--text-1)",
                          marginBottom: 2,
                        }}
                      >
                        🔴 Monday.com Procedures &amp; Workdocs
                      </div>
                      <div style={{ fontSize: 12, color: "var(--text-3)" }}>
                        Sync company SOPs, playbooks, and knowledge boards from
                        Monday.com.
                      </div>
                    </div>
                    {mondayConnectedUser ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#10B981",
                            background: "rgba(16, 185, 129, 0.1)",
                            padding: "4px 10px",
                            borderRadius: 8,
                          }}
                        >
                          <Check size={16} /> {mondayConnectedUser}
                        </span>
                        <button
                          type="button"
                          className="pill-btn primary"
                          onClick={() => {
                            setMondayModalOpen(true);
                            fetchMondayItems();
                          }}
                          style={{ fontSize: 12 }}
                        >
                          Browse & Import Boards
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="pill-btn primary"
                        onClick={() => {
                          setMondayModalOpen(true);
                          if (mondayApiKey) fetchMondayItems(mondayApiKey);
                        }}
                        style={{ fontSize: 12 }}
                      >
                        Connect Monday.com
                      </button>
                    )}
                  </div>
                  {/* SharePoint / Microsoft 365 Connect Card */}
                  <div
                    style={{
                      background: "rgba(0, 120, 212, 0.05)",
                      border: "1px solid rgba(0, 120, 212, 0.2)",
                      borderRadius: 12,
                      padding: "16px 20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 16,
                      flexWrap: "wrap",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: "var(--text-1)",
                          marginBottom: 2,
                        }}
                      >
                        🟦 SharePoint &amp; OneDrive SOPs
                      </div>
                      <div style={{ fontSize: 12, color: "var(--text-3)" }}>
                        Import Office 365 Word (.docx), PDF, or PowerPoint
                        procedure documents.
                      </div>
                    </div>
                    <button
                      type="button"
                      className="pill-btn primary"
                      onClick={() => setSharepointModalOpen(true)}
                      style={{ fontSize: 12 }}
                    >
                      Import SharePoint Doc
                    </button>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginTop: 8,
                    }}
                  >
                    <button
                      type="button"
                      className="ghost-btn"
                      onClick={() => setSetupStep(2)}
                    >
                      ← Back
                    </button>
                    <Button
                      type="button"
                      variant="primary"
                      disabled={creating}
                      onClick={handleFinishOnboarding}
                      style={{ minWidth: 220 }}
                    >
                      {creating
                        ? "Creating Company…"
                        : "Complete Setup & Launch SellSynq ✓"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            // ================= STATE 2: COMPANY HUB DASHBOARD =================
            <div
              style={{
                maxWidth: 960,
                margin: "0 auto",
                width: "100%",
                paddingBottom: 60,
              }}
            >
              {/* Header */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: 24,
                  flexWrap: "wrap",
                  gap: 16,
                }}
              >
                <div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      marginBottom: 6,
                    }}
                  >
                    <h1
                      style={{
                        fontSize: 26,
                        fontWeight: 700,
                        color: "var(--text-1)",
                        margin: 0,
                      }}
                    >
                      {data.organization.name}
                    </h1>
                    <span
                      style={{
                        padding: "4px 10px",
                        borderRadius: 20,
                        fontSize: 12,
                        fontWeight: 600,
                        background: isAdmin
                          ? "rgba(99, 102, 241, 0.15)"
                          : "var(--surface-hover)",
                        color: isAdmin ? "var(--primary)" : "var(--text-2)",
                        border: "1px solid var(--border-soft)",
                      }}
                    >
                      {data.isSuperAdmin
                        ? "Super Admin"
                        : isAdmin
                          ? "Company Admin"
                          : "Member"}
                    </span>
                    {data.organization.domain && (
                      <span style={{ fontSize: 13, color: "var(--text-3)" }}>
                        🌐 {data.organization.domain}
                      </span>
                    )}
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        background: "rgba(99, 102, 241, 0.08)",
                        border: "1px solid rgba(99, 102, 241, 0.2)",
                        borderRadius: 6,
                        padding: "3px 8px",
                        fontSize: 12,
                        fontFamily: "var(--font-mono, monospace)",
                        color: "var(--primary)",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        const url = `https://${data.organization?.subdomain || data.organization?.id}.sellsynq.com`;
                        navigator.clipboard.writeText(url);
                        toast.push({
                          tone: "success",
                          message: `Copied workspace link: ${url}`,
                        });
                      }}
                      title="Click to copy workspace address"
                    >
                      🔗 {data.organization?.subdomain || data.organization?.id}
                      .sellsynq.com
                    </span>
                  </div>
                  <p
                    style={{ fontSize: 14, color: "var(--text-2)", margin: 0 }}
                  >
                    Manage company profile, user roles, procedures, and team
                    members.
                  </p>
                </div>
                {/* AI Status Badge */}
                <div
                  style={{
                    background: "rgba(16, 185, 129, 0.1)",
                    border: "1px solid rgba(16, 185, 129, 0.25)",
                    borderRadius: 10,
                    padding: "8px 14px",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: 13,
                    color: "var(--gc-green, #10B981)",
                    fontWeight: 600,
                  }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: "#10B981",
                    }}
                  />
                  Live AI Grounding Active
                </div>
              </div>
              {/* User Active Role Switcher Card */}
              <div
                style={{
                  background: "var(--surface)",
                  border: "1px solid var(--border-soft)",
                  borderRadius: 14,
                  padding: "16px 20px",
                  marginBottom: 24,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 14,
                  boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div
                    style={{
                      width: 38,
                      height: 38,
                      borderRadius: 10,
                      background: "rgba(99, 102, 241, 0.12)",
                      color: "var(--primary)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Spark size={20} />
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: "var(--text-1)",
                      }}
                    >
                      Your Active AI Coaching Role:{" "}
                      <span style={{ color: "var(--primary)" }}>
                        {myCurrentRole || "Sales / Account Executive (AE)"}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: "var(--text-3)" }}>
                      Hints, talking points, and objection responses adapt in
                      real time to this role.
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <select
                    value={myCurrentRole}
                    onChange={(e) => handleUpdateMyRole(e.target.value)}
                    disabled={savingRole}
                    style={{
                      padding: "8px 12px",
                      borderRadius: 8,
                      border: "1px solid var(--border-soft)",
                      background: "var(--surface-hover)",
                      color: "var(--text-1)",
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    {myCurrentRole && !editRoles.includes(myCurrentRole) && (
                      <option value={myCurrentRole}>{myCurrentRole}</option>
                    )}
                    {editRoles.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {/* Navigation Tabs */}
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  borderBottom: "1px solid var(--border-soft)",
                  marginBottom: 28,
                }}
              >
                <button
                  type="button"
                  onClick={() => setActiveTab("profile")}
                  style={{
                    padding: "10px 18px",
                    fontSize: 14,
                    fontWeight: 600,
                    background: "transparent",
                    border: "none",
                    borderBottom:
                      activeTab === "profile"
                        ? "2px solid var(--primary)"
                        : "2px solid transparent",
                    color:
                      activeTab === "profile"
                        ? "var(--primary)"
                        : "var(--text-2)",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <Globe size={16} />
                  <span>Profile &amp; Roles</span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("docs")}
                  style={{
                    padding: "10px 18px",
                    fontSize: 14,
                    fontWeight: 600,
                    background: "transparent",
                    border: "none",
                    borderBottom:
                      activeTab === "docs"
                        ? "2px solid var(--primary)"
                        : "2px solid transparent",
                    color:
                      activeTab === "docs" ? "var(--primary)" : "var(--text-2)",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <Doc size={16} />
                  <span>
                    Knowledge Base ({data.documents.length})
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("team")}
                  style={{
                    padding: "10px 18px",
                    fontSize: 14,
                    fontWeight: 600,
                    background: "transparent",
                    border: "none",
                    borderBottom:
                      activeTab === "team"
                        ? "2px solid var(--primary)"
                        : "2px solid transparent",
                    color:
                      activeTab === "team" ? "var(--primary)" : "var(--text-2)",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <Users size={16} />
                  <span>Team ({data.members.length})</span>
                </button>
              </div>
              {/* Tab 1: Profile & Roles */}
              {activeTab === "profile" && (
                <div style={{ maxWidth: 840 }}>
                  <form
                    onSubmit={handleSaveProfile}
                    style={{
                      background: "var(--surface)",
                      border: "1px solid var(--border-soft)",
                      borderRadius: 16,
                      padding: 28,
                      display: "flex",
                      flexDirection: "column",
                      gap: 20,
                      boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
                      marginBottom: 24,
                    }}
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 16,
                      }}
                    >
                      <div>
                        <label
                          style={{
                            display: "block",
                            fontSize: 13,
                            fontWeight: 600,
                            color: "var(--text-1)",
                            marginBottom: 6,
                          }}
                        >
                          Company Name
                        </label>
                        <input
                          type="text"
                          required
                          disabled={!isAdmin}
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          style={{
                            width: "100%",
                            padding: "9px 12px",
                            borderRadius: 8,
                            border: "1px solid var(--border-soft)",
                            background: isAdmin
                              ? "var(--surface-hover)"
                              : "var(--surface)",
                            color: "var(--text-1)",
                            fontSize: 14,
                          }}
                        />
                      </div>
                      <div>
                        <label
                          style={{
                            display: "block",
                            fontSize: 13,
                            fontWeight: 600,
                            color: "var(--text-1)",
                            marginBottom: 6,
                          }}
                        >
                          Domain
                        </label>
                        <input
                          type="text"
                          disabled={!isAdmin}
                          value={editDomain}
                          onChange={(e) => setEditDomain(e.target.value)}
                          style={{
                            width: "100%",
                            padding: "9px 12px",
                            borderRadius: 8,
                            border: "1px solid var(--border-soft)",
                            background: isAdmin
                              ? "var(--surface-hover)"
                              : "var(--surface)",
                            color: "var(--text-1)",
                            fontSize: 14,
                          }}
                        />
                      </div>
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          fontSize: 13,
                          fontWeight: 600,
                          color: "var(--text-1)",
                          marginBottom: 6,
                        }}
                      >
                        Workspace Subdomain (.sellsynq.com)
                      </label>
                      <div
                        style={{
                          display: "flex",
                          gap: 10,
                          alignItems: "center",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            position: "relative",
                            flex: 1,
                          }}
                        >
                          <input
                            type="text"
                            disabled={!isAdmin}
                            value={editSubdomain}
                            onChange={(e) =>
                              setEditSubdomain(
                                e.target.value
                                  .toLowerCase()
                                  .replace(/[^a-z0-9-]/g, ""),
                              )
                            }
                            style={{
                              width: "100%",
                              padding: "9px 12px",
                              paddingRight: 140,
                              borderRadius: 8,
                              border: "1px solid var(--border-soft)",
                              background: isAdmin
                                ? "var(--surface-hover)"
                                : "var(--surface)",
                              color: "var(--text-1)",
                              fontSize: 14,
                              fontFamily: "var(--font-mono, monospace)",
                            }}
                          />
                          <span
                            style={{
                              position: "absolute",
                              right: 12,
                              color: "var(--text-3)",
                              fontSize: 13,
                              pointerEvents: "none",
                              fontFamily: "var(--font-mono, monospace)",
                            }}
                          >
                            .sellsynq.com
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const url = `https://${editSubdomain || "app"}.sellsynq.com`;
                            navigator.clipboard.writeText(url);
                            toast.push({
                              tone: "success",
                              message: `Copied workspace URL: ${url}`,
                            });
                          }}
                          className="pill-btn secondary sm"
                          style={{
                            whiteSpace: "nowrap",
                            padding: "8px 14px",
                            fontSize: 13,
                          }}
                          title="Copy direct link to your company workspace"
                        >
                          📋 Copy Workspace URL
                        </button>
                      </div>
                      <div
                        style={{
                          fontSize: 11.5,
                          color: "var(--text-3)",
                          marginTop: 4,
                          marginBottom: 16,
                        }}
                      >
                        Direct workspace address:{" "}
                        <strong style={{ color: "var(--primary)" }}>
                          https://{editSubdomain || "yourcompany"}.sellsynq.com
                        </strong>
                      </div>
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          fontSize: 13,
                          fontWeight: 600,
                          color: "var(--text-1)",
                          marginBottom: 4,
                        }}
                      >
                        Allowed Email Domains &amp; Websites
                      </label>
                      <div
                        style={{
                          fontSize: 12,
                          color: "var(--text-3)",
                          marginBottom: 8,
                        }}
                      >
                        Only users whose email address ends with these domains will be permitted to access this workspace (e.g. <code>wideops.com</code>, <code>wideops.co.il</code>).
                      </div>
                      {isAdmin && (
                        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                          <input
                            type="text"
                            placeholder="e.g. wideops.co.il"
                            value={editDomainInput}
                            onChange={(e) => setEditDomainInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleAddEditAllowedDomain();
                              }
                            }}
                            style={{
                              flex: 1,
                              padding: "8px 12px",
                              borderRadius: 8,
                              border: "1px solid var(--border-soft)",
                              background: "var(--surface-hover)",
                              color: "var(--text-1)",
                              fontSize: 13,
                            }}
                          />
                          <button
                            type="button"
                            className="pill-btn secondary sm"
                            onClick={handleAddEditAllowedDomain}
                            style={{ whiteSpace: "nowrap", padding: "8px 14px", fontSize: 13 }}
                          >
                            + Add Domain
                          </button>
                        </div>
                      )}
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
                        {editAllowedDomains.map((d) => (
                          <span
                            key={d}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 6,
                              padding: "4px 10px",
                              borderRadius: 16,
                              background: "var(--surface-hover)",
                              border: "1px solid var(--border-soft)",
                              fontSize: 12,
                              fontWeight: 500,
                              color: "var(--text-1)",
                            }}
                          >
                            @{d}
                            {isAdmin && (
                              <button
                                type="button"
                                onClick={() => handleRemoveEditAllowedDomain(d)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                  color: "var(--text-3)",
                                  padding: "0 2px",
                                  fontSize: 14,
                                  lineHeight: 1,
                                }}
                              >
                                ×
                              </button>
                            )}
                          </span>
                        ))}
                        {editAllowedDomains.length === 0 && (
                          <span style={{ fontSize: 12, color: "var(--text-3)" }}>
                            No domain restrictions set (all authenticated company users allowed).
                          </span>
                        )}
                      </div>
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          fontSize: 13,
                          fontWeight: 600,
                          color: "var(--text-1)",
                          marginBottom: 6,
                        }}
                      >
                        Company Overview &amp; Description
                      </label>
                      <textarea
                        rows={2}
                        disabled={!isAdmin}
                        value={editDesc}
                        onChange={(e) => setEditDesc(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "9px 12px",
                          borderRadius: 8,
                          border: "1px solid var(--border-soft)",
                          background: isAdmin
                            ? "var(--surface-hover)"
                            : "var(--surface)",
                          color: "var(--text-1)",
                          fontSize: 14,
                        }}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          fontSize: 13,
                          fontWeight: 600,
                          color: "var(--text-1)",
                          marginBottom: 6,
                        }}
                      >
                        Products &amp; Solutions
                      </label>
                      <textarea
                        rows={2}
                        disabled={!isAdmin}
                        value={editProducts}
                        onChange={(e) => setEditProducts(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "9px 12px",
                          borderRadius: 8,
                          border: "1px solid var(--border-soft)",
                          background: isAdmin
                            ? "var(--surface-hover)"
                            : "var(--surface)",
                          color: "var(--text-1)",
                          fontSize: 14,
                        }}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          fontSize: 13,
                          fontWeight: 600,
                          color: "var(--text-1)",
                          marginBottom: 6,
                        }}
                      >
                        Value Proposition
                      </label>
                      <textarea
                        rows={2}
                        disabled={!isAdmin}
                        value={editValueProp}
                        onChange={(e) => setEditValueProp(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "9px 12px",
                          borderRadius: 8,
                          border: "1px solid var(--border-soft)",
                          background: isAdmin
                            ? "var(--surface-hover)"
                            : "var(--surface)",
                          color: "var(--text-1)",
                          fontSize: 14,
                        }}
                      />
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 16,
                      }}
                    >
                      <div>
                        <label
                          style={{
                            display: "block",
                            fontSize: 13,
                            fontWeight: 600,
                            color: "var(--text-1)",
                            marginBottom: 6,
                          }}
                        >
                          Target Audience / ICP
                        </label>
                        <input
                          type="text"
                          disabled={!isAdmin}
                          value={editAudience}
                          onChange={(e) => setEditAudience(e.target.value)}
                          style={{
                            width: "100%",
                            padding: "9px 12px",
                            borderRadius: 8,
                            border: "1px solid var(--border-soft)",
                            background: isAdmin
                              ? "var(--surface-hover)"
                              : "var(--surface)",
                            color: "var(--text-1)",
                            fontSize: 14,
                          }}
                        />
                      </div>
                      <div>
                        <label
                          style={{
                            display: "block",
                            fontSize: 13,
                            fontWeight: 600,
                            color: "var(--text-1)",
                            marginBottom: 6,
                          }}
                        >
                          Known Competitors
                        </label>
                        <input
                          type="text"
                          disabled={!isAdmin}
                          value={editCompetitors}
                          onChange={(e) => setEditCompetitors(e.target.value)}
                          style={{
                            width: "100%",
                            padding: "9px 12px",
                            borderRadius: 8,
                            border: "1px solid var(--border-soft)",
                            background: isAdmin
                              ? "var(--surface-hover)"
                              : "var(--surface)",
                            color: "var(--text-1)",
                            fontSize: 14,
                          }}
                        />
                      </div>
                    </div>
                    {/* Roles Management (Admin) */}
                    <div
                      style={{
                        borderTop: "1px solid var(--border-soft)",
                        paddingTop: 18,
                      }}
                    >
                      <label
                        style={{
                          display: "block",
                          fontSize: 14,
                          fontWeight: 700,
                          color: "var(--text-1)",
                          marginBottom: 6,
                        }}
                      >
                        Configured Company Roles
                      </label>
                      <p
                        style={{
                          fontSize: 12,
                          color: "var(--text-3)",
                          marginBottom: 12,
                        }}
                      >
                        Roles configured here are available for all team
                        members. AI live recommendations adapt to each role.
                      </p>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 8,
                          marginBottom: 14,
                        }}
                      >
                        {editRoles.map((role) => (
                          <div
                            key={role}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 8,
                              padding: "6px 12px",
                              borderRadius: 8,
                              background: "rgba(99, 102, 241, 0.08)",
                              border: "1px solid rgba(99, 102, 241, 0.25)",
                              fontSize: 13,
                              color: "var(--text-1)",
                            }}
                          >
                            <span>{role}</span>
                            {isAdmin && (
                              <button
                                type="button"
                                onClick={() => handleRemoveExistingRole(role)}
                                style={{
                                  background: "transparent",
                                  border: "none",
                                  color: "var(--text-3)",
                                  cursor: "pointer",
                                  padding: 2,
                                  fontSize: 14,
                                }}
                                title="Remove role"
                              >
                                ×
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      {isAdmin && (
                        <div style={{ display: "flex", gap: 8 }}>
                          <input
                            type="text"
                            placeholder="Add another role (e.g. Marketing, Account Manager, Sales Rep, BDR)…"
                            value={customRoleInput}
                            onChange={(e) => setCustomRoleInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleAddExistingRole();
                              }
                            }}
                            style={{
                              flex: 1,
                              padding: "8px 12px",
                              borderRadius: 8,
                              border: "1px solid var(--border-soft)",
                              background: "var(--surface-hover)",
                              color: "var(--text-1)",
                              fontSize: 13,
                            }}
                          />
                          <button
                            type="button"
                            className="pill-btn primary"
                            onClick={handleAddExistingRole}
                            disabled={!customRoleInput.trim()}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <Plus size={15} />
                            <span>Add</span>
                          </button>
                        </div>
                      )}
                      {isAdmin && DEFAULT_COMPANY_ROLES.some((r) => !editRoles.includes(r)) && (
                        <div
                          style={{
                            marginTop: 10,
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 6,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 12,
                              color: "var(--text-3)",
                              fontWeight: 500,
                            }}
                          >
                            Quick Suggestions:
                          </span>
                          {DEFAULT_COMPANY_ROLES.filter((r) => !editRoles.includes(r)).map((sug) => (
                            <button
                              key={sug}
                              type="button"
                              onClick={() => setEditRoles([...editRoles, sug])}
                              style={{
                                border: "1px dashed rgba(99, 102, 241, 0.4)",
                                background: "rgba(99, 102, 241, 0.05)",
                                color: "var(--brand-primary, #6366f1)",
                                borderRadius: 6,
                                padding: "4px 8px",
                                fontSize: 12,
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <Plus size={12} />
                              {sug}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {isAdmin && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                          marginTop: 8,
                        }}
                      >
                        <Button
                          type="submit"
                          variant="primary"
                          disabled={saving}
                        >
                          {saving ? "Saving…" : "Save Company Changes"}
                        </Button>
                      </div>
                    )}
                  </form>

                  {/* Knowledge Base & Document Connectors Hub */}
                  <div
                    style={{
                      marginTop: 28,
                      background: "var(--surface)",
                      border: "1px solid var(--border-soft)",
                      borderRadius: 16,
                      padding: 24,
                      boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 12,
                        marginBottom: 16,
                      }}
                    >
                      <div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <span style={{ fontSize: 20 }}>📚</span>
                          <h3
                            style={{
                              fontSize: 16,
                              fontWeight: 700,
                              margin: 0,
                              color: "var(--text-1)",
                            }}
                          >
                            Knowledge Base &amp; Learning Connectors
                          </h3>
                        </div>
                        <p
                          style={{
                            fontSize: 12.5,
                            color: "var(--text-3)",
                            margin: "4px 0 0",
                          }}
                        >
                          Connect company documents so SellSynq AI learns your products, pricing, and SOPs for live meeting copilot.
                        </p>
                      </div>
                      <button
                        type="button"
                        className="pill-btn primary"
                        onClick={() => setActiveTab("docs")}
                        style={{ fontSize: 12, padding: "6px 14px" }}
                      >
                        View Knowledge Base ({data.documents.length}) →
                      </button>
                    </div>

                    {/* Quick Connectors Grid */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
                        gap: 12,
                      }}
                    >
                      {/* Monday.com */}
                      <button
                        type="button"
                        onClick={() => {
                          setMondayModalOpen(true);
                          fetchMondayItems();
                        }}
                        style={{
                          padding: "14px 16px",
                          borderRadius: 12,
                          background: mondayConnectedUser ? "rgba(226, 74, 104, 0.08)" : "var(--surface-hover)",
                          border: mondayConnectedUser ? "1px solid rgba(226, 74, 104, 0.3)" : "1px solid var(--border-soft)",
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          cursor: "pointer",
                          textAlign: "left",
                          transition: "all 0.15s ease",
                        }}
                      >
                        <span style={{ fontSize: 24 }}>🔴</span>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: mondayConnectedUser ? "#E24A68" : "var(--text-1)" }}>
                            Monday.com {mondayConnectedUser ? "✓" : ""}
                          </div>
                          <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>
                            {mondayConnectedUser ? "Connected (Boards & Docs)" : "Connect Boards & Docs"}
                          </div>
                        </div>
                      </button>

                      {/* SharePoint / Microsoft 365 */}
                      <button
                        type="button"
                        onClick={() => setSharepointModalOpen(true)}
                        style={{
                          padding: "14px 16px",
                          borderRadius: 12,
                          background: "var(--surface-hover)",
                          border: "1px solid var(--border-soft)",
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          cursor: "pointer",
                          textAlign: "left",
                          transition: "all 0.15s ease",
                        }}
                      >
                        <span style={{ fontSize: 24 }}>🟦</span>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-1)" }}>
                            SharePoint / OneDrive
                          </div>
                          <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>
                            Import Office 365 &amp; Word SOPs
                          </div>
                        </div>
                      </button>

                      {/* Google Drive */}
                      <button
                        type="button"
                        onClick={googleToken ? undefined : handleConnectDrive}
                        style={{
                          padding: "14px 16px",
                          borderRadius: 12,
                          background: googleToken ? "rgba(16, 185, 129, 0.08)" : "var(--surface-hover)",
                          border: googleToken ? "1px solid rgba(16, 185, 129, 0.3)" : "1px solid var(--border-soft)",
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          cursor: googleToken ? "default" : "pointer",
                          textAlign: "left",
                          transition: "all 0.15s ease",
                        }}
                      >
                        <span style={{ fontSize: 24 }}>🔷</span>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: googleToken ? "#10B981" : "var(--text-1)" }}>
                            Google Drive {googleToken ? "✓" : ""}
                          </div>
                          <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>
                            {googleToken ? "Connected (Docs & Sheets)" : "Sync Google Docs & Sheets"}
                          </div>
                        </div>
                      </button>

                      {/* Upload Files */}
                      <button
                        type="button"
                        onClick={() => {
                          setActiveTab("docs");
                          fileInputRef.current?.click();
                        }}
                        style={{
                          padding: "14px 16px",
                          borderRadius: 12,
                          background: "var(--surface-hover)",
                          border: "1px solid var(--border-soft)",
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          cursor: "pointer",
                          textAlign: "left",
                          transition: "all 0.15s ease",
                        }}
                      >
                        <span style={{ fontSize: 24 }}>📁</span>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-1)" }}>
                            Upload Files
                          </div>
                          <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>
                            PDF, Word, PPTX, TXT
                          </div>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {/* Tab 2: Procedures & Knowledge Documents */}
              {activeTab === "docs" && (
                <div style={{ maxWidth: 880 }}>
                  {/* Live Procedures & SOPs Box */}
                  <div
                    style={{
                      background: "var(--surface)",
                      border: "1px solid var(--border-soft)",
                      borderRadius: 16,
                      padding: 24,
                      marginBottom: 24,
                      boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: 12,
                      }}
                    >
                      <div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <span style={{ fontSize: 18 }}>📋</span>
                          <h3
                            style={{
                              fontSize: 17,
                              fontWeight: 700,
                              margin: 0,
                              color: "var(--text-1)",
                            }}
                          >
                            Live Company Procedures &amp; Policy Rules (SOPs)
                          </h3>
                        </div>
                        <p
                          style={{
                            fontSize: 13,
                            color: "var(--text-2)",
                            margin: "4px 0 0",
                            lineHeight: 1.4,
                          }}
                        >
                          Directives outlined here are strictly enforced by
                          SellSynq Live Copilot during customer calls.
                        </p>
                      </div>
                      {isAdmin && (
                        <Button
                          type="button"
                          variant="primary"
                          disabled={savingProcedures}
                          onClick={handleSaveProcedures}
                          style={{ padding: "6px 16px", fontSize: 13 }}
                        >
                          {savingProcedures ? "Saving…" : "Save Procedures"}
                        </Button>
                      )}
                    </div>
                    <textarea
                      rows={5}
                      disabled={!isAdmin}
                      placeholder={`Enter pricing limits, return policies, escalation paths, and standard operating procedures (SOPs)...`}
                      value={editProceduresText}
                      onChange={(e) => setEditProceduresText(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "12px 14px",
                        borderRadius: 8,
                        border: "1px solid var(--border-soft)",
                        background: isAdmin
                          ? "var(--surface-hover)"
                          : "var(--surface)",
                        color: "var(--text-1)",
                        fontSize: 13,
                        lineHeight: 1.5,
                      }}
                    />
                  </div>
                  {/* Upload & Connector Hub */}
                  {isAdmin && (
                    <div
                      style={{
                        background: "var(--surface)",
                        border: "1px solid var(--border-soft)",
                        borderRadius: 16,
                        padding: 22,
                        marginBottom: 24,
                        display: "flex",
                        flexDirection: "column",
                        gap: 16,
                        boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: 12,
                        }}
                      >
                        <div>
                          <div
                            style={{
                              fontSize: 16,
                              fontWeight: 700,
                              color: "var(--text-1)",
                            }}
                          >
                            Add Company Knowledge &amp; Procedures
                          </div>
                          <div
                            style={{
                              fontSize: 12,
                              color: "var(--text-3)",
                              marginTop: 2,
                            }}
                          >
                            Import company SOPs, playbooks, and guidelines
                            directly from your team's tools.
                          </div>
                        </div>
                        {/* Category Selector */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: "var(--text-2)",
                            }}
                          >
                            Upload Category:
                          </span>
                          <select
                            value={uploadCategory}
                            onChange={(e) =>
                              setUploadCategory(e.target.value as any)
                            }
                            style={{
                              padding: "6px 12px",
                              borderRadius: 8,
                              border: "1px solid var(--border-soft)",
                              background: "var(--surface-hover)",
                              color: "var(--text-1)",
                              fontSize: 12,
                              fontWeight: 600,
                            }}
                          >
                            <option value="procedure">
                              📋 Company Procedure (SOP - Live Grounding)
                            </option>
                            <option value="knowledge">
                              📚 General Knowledge Base
                            </option>
                          </select>
                        </div>
                      </div>
                      {/* Connectors Quick Bar */}
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fit, minmax(180px, 1fr))",
                          gap: 10,
                        }}
                      >
                        {/* 1. Local File Upload */}
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          accept=".pdf,.doc,.docx,.ppt,.pptx,.txt,.md"
                          style={{ display: "none" }}
                          onChange={(e) => handleFileUpload(e.target.files)}
                        />
                        {userAllowedConnectors.includes("uploads") && (
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadingDoc}
                          style={{
                            padding: "12px 14px",
                            borderRadius: 10,
                            background: "var(--surface-hover)",
                            border: "1px solid var(--border-soft)",
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            cursor: "pointer",
                            textAlign: "left",
                            transition: "all 0.15s ease",
                          }}
                        >
                          <span style={{ fontSize: 22 }}>📁</span>
                          <div>
                            <div
                              style={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: "var(--text-1)",
                              }}
                            >
                              {uploadingDoc ? "Uploading…" : "Upload Files"}
                            </div>
                            <div
                              style={{ fontSize: 11, color: "var(--text-3)" }}
                            >
                              PDF, Word, PPTX, TXT
                            </div>
                          </div>
                        </button>
                        )}
                        {/* 2. Google Drive */}
                        {userAllowedConnectors.includes("googleDrive") && (
                        <button
                          type="button"
                          onClick={googleToken ? undefined : handleConnectDrive}
                          style={{
                            padding: "12px 14px",
                            borderRadius: 10,
                            background: googleToken
                              ? "rgba(16, 185, 129, 0.08)"
                              : "var(--surface-hover)",
                            border: googleToken
                              ? "1px solid rgba(16, 185, 129, 0.3)"
                              : "1px solid var(--border-soft)",
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            cursor: googleToken ? "default" : "pointer",
                            textAlign: "left",
                            transition: "all 0.15s ease",
                          }}
                        >
                          <span style={{ fontSize: 22 }}>🔷</span>
                          <div>
                            <div
                              style={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: googleToken
                                  ? "#10B981"
                                  : "var(--text-1)",
                              }}
                            >
                              {googleToken ? "Google Drive ✓" : "Google Drive"}
                            </div>
                            <div
                              style={{ fontSize: 11, color: "var(--text-3)" }}
                            >
                              {googleToken ? "Connected" : "Sync Docs & Drive"}
                            </div>
                          </div>
                        </button>
                        )}
                        {/* 3. Monday.com Connector */}
                        {userAllowedConnectors.includes("monday") && (
                        <button
                          type="button"
                          onClick={() => {
                            setMondayModalOpen(true);
                            if (mondayApiKey) fetchMondayItems(mondayApiKey);
                          }}
                          style={{
                            padding: "12px 14px",
                            borderRadius: 10,
                            background: mondayConnectedUser
                              ? "rgba(226, 74, 104, 0.08)"
                              : "var(--surface-hover)",
                            border: mondayConnectedUser
                              ? "1px solid rgba(226, 74, 104, 0.3)"
                              : "1px solid var(--border-soft)",
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            cursor: "pointer",
                            textAlign: "left",
                            transition: "all 0.15s ease",
                          }}
                        >
                          <span style={{ fontSize: 22 }}>🔴</span>
                          <div>
                            <div
                              style={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: mondayConnectedUser
                                  ? "#E24A68"
                                  : "var(--text-1)",
                              }}
                            >
                              Monday.com {mondayConnectedUser ? "✓" : ""}
                            </div>
                            <div
                              style={{ fontSize: 11, color: "var(--text-3)" }}
                            >
                              {mondayConnectedUser
                                ? "Connected"
                                : "Workdocs & Boards"}
                            </div>
                          </div>
                        </button>
                        )}
                        {/* 4. SharePoint Connector */}
                        {userAllowedConnectors.includes("sharepoint") && (
                        <button
                          type="button"
                          onClick={() => setSharepointModalOpen(true)}
                          style={{
                            padding: "12px 14px",
                            borderRadius: 10,
                            background: "var(--surface-hover)",
                            border: "1px solid var(--border-soft)",
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            cursor: "pointer",
                            textAlign: "left",
                            transition: "all 0.15s ease",
                          }}
                        >
                          <span style={{ fontSize: 22 }}>🟦</span>
                          <div>
                            <div
                              style={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: "var(--text-1)",
                              }}
                            >
                              SharePoint / M365
                            </div>
                            <div
                              style={{ fontSize: 11, color: "var(--text-3)" }}
                            >
                              Word, OneDrive, Docs
                            </div>
                          </div>
                        </button>
                        )}
                      </div>
                      {/* Universal Link Importer */}
                      <div
                        style={{
                          display: "flex",
                          gap: 10,
                          alignItems: "center",
                          flexWrap: "wrap",
                          paddingTop: 8,
                          borderTop: "1px dashed var(--border-soft)",
                        }}
                      >
                        <LinkIcon size={16} />
                        <input
                          type="url"
                          placeholder="Paste any link: Monday.com doc/board, SharePoint, OneDrive, Google Doc, or web URL…"
                          value={docLinkUrl}
                          onChange={(e) => setDocLinkUrl(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleAddLink();
                          }}
                          style={{
                            flex: 1,
                            minWidth: 260,
                            padding: "9px 12px",
                            borderRadius: 8,
                            border: "1px solid var(--border-soft)",
                            background: "var(--surface-hover)",
                            color: "var(--text-1)",
                            fontSize: 13,
                          }}
                        />
                        <select
                          value={linkCategory}
                          onChange={(e) =>
                            setLinkCategory(e.target.value as any)
                          }
                          style={{
                            padding: "9px 10px",
                            borderRadius: 8,
                            border: "1px solid var(--border-soft)",
                            background: "var(--surface-hover)",
                            color: "var(--text-1)",
                            fontSize: 12,
                          }}
                        >
                          <option value="procedure">
                            Company Procedure (SOP)
                          </option>
                          <option value="knowledge">Knowledge Doc</option>
                        </select>
                        <Button
                          type="button"
                          variant="primary"
                          disabled={addingLink || !docLinkUrl.trim()}
                          onClick={handleAddLink}
                          style={{ fontSize: 12, padding: "8px 16px" }}
                        >
                          {addingLink ? "Indexing…" : "Add & Index"}
                        </Button>
                      </div>
                    </div>
                  )}
                  {/* Documents Table */}
                  {displayedDocs.length === 0 ? (
                    <div
                      style={{
                        padding: 48,
                        textAlign: "center",
                        background: "var(--surface)",
                        border: "1px dashed var(--border-soft)",
                        borderRadius: 14,
                        color: "var(--text-3)",
                      }}
                    >
                      <Doc size={32} />
                      <div
                        style={{
                          marginTop: 12,
                          fontWeight: 600,
                          fontSize: 15,
                          color: "var(--text-2)",
                        }}
                      >
                        No company documents indexed yet
                      </div>
                      <p style={{ fontSize: 13, marginTop: 4 }}>
                        Upload your SOPs, procedure docs, or pitch decks to
                        ground your team's live calls.
                      </p>
                    </div>
                  ) : (
                    <div
                      style={{
                        background: "var(--surface)",
                        border: "1px solid var(--border-soft)",
                        borderRadius: 14,
                        overflow: "hidden",
                        boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
                      }}
                    >
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          fontSize: 13,
                        }}
                      >
                        <thead>
                          <tr
                            style={{
                              borderBottom: "1px solid var(--border-soft)",
                              background: "var(--surface-hover)",
                              textAlign: "left",
                            }}
                          >
                            <th
                              style={{
                                padding: "12px 18px",
                                fontWeight: 600,
                                color: "var(--text-2)",
                              }}
                            >
                              Document Name
                            </th>
                            <th
                              style={{
                                padding: "12px 18px",
                                fontWeight: 600,
                                color: "var(--text-2)",
                              }}
                            >
                              Source
                            </th>
                            <th
                              style={{
                                padding: "12px 18px",
                                fontWeight: 600,
                                color: "var(--text-2)",
                              }}
                            >
                              Category
                            </th>
                            <th
                              style={{
                                padding: "12px 18px",
                                fontWeight: 600,
                                color: "var(--text-2)",
                              }}
                            >
                              Status
                            </th>
                            <th
                              style={{
                                padding: "12px 18px",
                                fontWeight: 600,
                                color: "var(--text-2)",
                              }}
                            >
                              Added
                            </th>
                            <th
                              style={{
                                padding: "12px 18px",
                                fontWeight: 600,
                                color: "var(--text-2)",
                                textAlign: "right",
                              }}
                            >
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {displayedDocs.map((doc) => {
                            const isLink = Boolean(doc.sourceUrl);
                            const href =
                              safeHref(doc.sourceUrl) ??
                              `/organizations/documents/${doc.id}/file`;
                            const isProcedure =
                              doc.category === "procedure" ||
                              doc.name.toLowerCase().includes("procedure") ||
                              doc.name.toLowerCase().includes("נוהל") ||
                              doc.name.toLowerCase().includes("sop");
                            return (
                              <tr
                                key={doc.id}
                                style={{
                                  borderBottom: "1px solid var(--border-soft)",
                                }}
                              >
                                <td
                                  style={{
                                    padding: "12px 18px",
                                    fontWeight: 600,
                                    color: "var(--text-1)",
                                  }}
                                >
                                  <a
                                    href={href}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{
                                      color: "var(--text-1)",
                                      textDecoration: "none",
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 8,
                                    }}
                                  >
                                    <span>{isLink ? "🔗" : "📄"}</span>
                                    <span>{doc.name}</span>
                                  </a>
                                </td>
                                <td style={{ padding: "12px 18px" }}>
                                  {doc.source === "monday" ? (
                                    <span
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 5,
                                        padding: "3px 8px",
                                        borderRadius: 6,
                                        fontSize: 11,
                                        fontWeight: 600,
                                        background: "rgba(226, 74, 104, 0.12)",
                                        color: "#E24A68",
                                      }}
                                    >
                                      🔴 Monday.com
                                    </span>
                                  ) : doc.source === "sharepoint" ? (
                                    <span
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 5,
                                        padding: "3px 8px",
                                        borderRadius: 6,
                                        fontSize: 11,
                                        fontWeight: 600,
                                        background: "rgba(0, 120, 212, 0.12)",
                                        color: "#0078D4",
                                      }}
                                    >
                                      🟦 SharePoint
                                    </span>
                                  ) : doc.source === "gdrive" ? (
                                    <span
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 5,
                                        padding: "3px 8px",
                                        borderRadius: 6,
                                        fontSize: 11,
                                        fontWeight: 600,
                                        background: "rgba(37, 99, 235, 0.12)",
                                        color: "#2563EB",
                                      }}
                                    >
                                      🔷 Google Drive
                                    </span>
                                  ) : doc.source === "upload" ? (
                                    <span
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 5,
                                        padding: "3px 8px",
                                        borderRadius: 6,
                                        fontSize: 11,
                                        fontWeight: 600,
                                        background: "rgba(100, 116, 139, 0.12)",
                                        color: "var(--text-2)",
                                      }}
                                    >
                                      📁 File Upload
                                    </span>
                                  ) : (
                                    <span
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 5,
                                        padding: "3px 8px",
                                        borderRadius: 6,
                                        fontSize: 11,
                                        fontWeight: 600,
                                        background: "rgba(100, 116, 139, 0.12)",
                                        color: "var(--text-2)",
                                      }}
                                    >
                                      🌐 Web Link
                                    </span>
                                  )}
                                </td>
                                <td style={{ padding: "12px 18px" }}>
                                  <span
                                    style={{
                                      display: "inline-block",
                                      padding: "3px 8px",
                                      borderRadius: 6,
                                      fontSize: 11,
                                      fontWeight: 600,
                                      background: isProcedure
                                        ? "rgba(99, 102, 241, 0.12)"
                                        : "rgba(100, 116, 139, 0.1)",
                                      color: isProcedure
                                        ? "var(--primary)"
                                        : "var(--text-2)",
                                    }}
                                  >
                                    {isProcedure
                                      ? "📋 Procedure (SOP)"
                                      : "📚 Knowledge Document"}
                                  </span>
                                </td>
                                <td style={{ padding: "12px 18px" }}>
                                  <span
                                    style={{
                                      display: "inline-block",
                                      padding: "3px 8px",
                                      borderRadius: 12,
                                      fontSize: 11,
                                      fontWeight: 600,
                                      background:
                                        doc.status === "ready"
                                          ? "rgba(16, 185, 129, 0.12)"
                                          : doc.status === "failed"
                                            ? "rgba(239, 68, 68, 0.12)"
                                            : "rgba(245, 158, 11, 0.12)",
                                      color:
                                        doc.status === "ready"
                                          ? "#10B981"
                                          : doc.status === "failed"
                                            ? "#EF4444"
                                            : "#F59E0B",
                                    }}
                                  >
                                    {doc.status.toUpperCase()}
                                  </span>
                                </td>
                                <td
                                  style={{
                                    padding: "12px 18px",
                                    color: "var(--text-3)",
                                  }}
                                >
                                  {new Date(doc.createdAt).toLocaleDateString()}
                                </td>
                                <td
                                  style={{
                                    padding: "12px 18px",
                                    textAlign: "right",
                                  }}
                                >
                                  {isAdmin && (
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteDoc(doc.id)}
                                      style={{
                                        background: "transparent",
                                        border: "none",
                                        color: "var(--gc-red, #EF4444)",
                                        cursor: "pointer",
                                        padding: 6,
                                      }}
                                      title="Delete document"
                                    >
                                      <Trash size={15} />
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
              {/* Tab 3: Team Members */}
              {activeTab === "team" && (
                <div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 20,
                      flexWrap: "wrap",
                      gap: 12,
                    }}
                  >
                    <div>
                      <h3
                        style={{
                          fontSize: 18,
                          fontWeight: 700,
                          margin: "0 0 4px",
                          color: "var(--text-1)",
                        }}
                      >
                        Team Directory, Hierarchy &amp; Connectors
                      </h3>
                      <p
                        style={{
                          fontSize: 13,
                          color: "var(--text-3)",
                          margin: 0,
                        }}
                      >
                        Manage organizational reporting ("Reports To"), manager oversight, and per-user data connector access.
                      </p>
                    </div>
                    {(data.role === "admin" || data.isSuperAdmin) && (
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => {
                          setInviteEmail("");
                          setInviteName("");
                          setInviteRole(data.organization?.roles?.[0] || DEFAULT_COMPANY_ROLES[0] || "");
                          setInviteManagerUid("");
                          setInviteCompanyRole("member");
                          setInviteConnectors(["uploads", "googleDrive", "monday", "sharepoint"]);
                          setInviteResult(null);
                          setInviteModalOpen(true);
                        }}
                        style={{ gap: 6, display: "inline-flex", alignItems: "center" }}
                      >
                        <Plus size={14} /> Invite Member
                      </Button>
                    )}
                  </div>
                  <div
                    style={{
                      background: "var(--surface)",
                      border: "1px solid var(--border-soft)",
                      borderRadius: 14,
                      overflow: "hidden",
                      boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
                    }}
                  >
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: 13,
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            borderBottom: "1px solid var(--border-soft)",
                            background: "var(--surface-hover)",
                            textAlign: "left",
                          }}
                        >
                          <th style={{ padding: "12px 18px", fontWeight: 600, color: "var(--text-2)" }}>Member</th>
                          <th style={{ padding: "12px 18px", fontWeight: 600, color: "var(--text-2)" }}>Email</th>
                          <th style={{ padding: "12px 18px", fontWeight: 600, color: "var(--text-2)" }}>Coaching Role</th>
                          <th style={{ padding: "12px 18px", fontWeight: 600, color: "var(--text-2)" }}>Reports To</th>
                          <th style={{ padding: "12px 18px", fontWeight: 600, color: "var(--text-2)" }}>Allowed Connectors</th>
                          <th style={{ padding: "12px 18px", fontWeight: 600, color: "var(--text-2)" }}>Access</th>
                          <th style={{ padding: "12px 18px", fontWeight: 600, color: "var(--text-2)", textAlign: "right" }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.members.map((m) => {
                          const isDirectManager = m.managerUid === user?.uid;
                          const canViewOverview = Boolean(data.role === "admin" || data.isSuperAdmin || isDirectManager || m.uid === user?.uid);
                          const canEdit = Boolean(data.role === "admin" || data.isSuperAdmin);
                          const isSelf = m.uid === user?.uid;
                          const connectors = m.allowedConnectors && m.allowedConnectors.length > 0
                            ? m.allowedConnectors
                            : (["uploads", "googleDrive", "monday", "sharepoint"] as ConnectorType[]);

                          return (
                            <tr
                              key={m.uid}
                              style={{
                                borderBottom: "1px solid var(--border-soft)",
                              }}
                            >
                              <td style={{ padding: "12px 18px", fontWeight: 600, color: "var(--text-1)" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <div
                                    style={{
                                      width: 28,
                                      height: 28,
                                      borderRadius: "50%",
                                      background: "var(--surface-hover)",
                                      border: "1px solid var(--border-soft)",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontSize: 12,
                                      fontWeight: 700,
                                      color: "var(--primary)",
                                    }}
                                  >
                                    {(m.name || m.email || "U").charAt(0).toUpperCase()}
                                  </div>
                                  <div>
                                    <div>{m.name || m.email.split("@")[0]}</div>
                                    {m.status === "invited" && (
                                      <span
                                        style={{
                                          fontSize: 10,
                                          background: "rgba(16, 185, 129, 0.15)",
                                          color: "#10b981",
                                          padding: "1px 6px",
                                          borderRadius: 4,
                                          fontWeight: 600,
                                        }}
                                      >
                                        ✓ Invited (Pre-Approved)
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td style={{ padding: "12px 18px", color: "var(--text-2)" }}>{m.email}</td>
                              <td style={{ padding: "12px 18px", color: "var(--text-1)", fontWeight: 500 }}>
                                {cleanRoleName(m.functionalRole || m.role || "Sales / Account Executive (AE)")}
                              </td>
                              <td style={{ padding: "12px 18px", color: "var(--text-2)" }}>
                                {m.managerName ? (
                                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                    👤 {m.managerName}
                                  </span>
                                ) : (
                                  <span style={{ color: "var(--text-3)" }}>—</span>
                                )}
                              </td>
                              <td style={{ padding: "12px 18px" }}>
                                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                                  {connectors.map((c) => (
                                    <span
                                      key={c}
                                      style={{
                                        fontSize: 10,
                                        padding: "2px 6px",
                                        borderRadius: 6,
                                        background: "var(--surface-hover)",
                                        border: "1px solid var(--border-soft)",
                                        color: "var(--text-2)",
                                        fontWeight: 500,
                                      }}
                                    >
                                      {c === "googleDrive" && "🔷 Drive"}
                                      {c === "monday" && "🔴 Monday"}
                                      {c === "sharepoint" && "🟦 SharePoint"}
                                      {c === "uploads" && "📁 Uploads"}
                                    </span>
                                  ))}
                                </div>
                              </td>
                              <td style={{ padding: "12px 18px" }}>
                                <span
                                  style={{
                                    padding: "3px 8px",
                                    borderRadius: 10,
                                    fontSize: 11,
                                    fontWeight: 600,
                                    background:
                                      m.companyRole === "admin"
                                        ? "rgba(99, 102, 241, 0.12)"
                                        : "var(--surface-hover)",
                                    color:
                                      m.companyRole === "admin"
                                        ? "var(--primary)"
                                        : "var(--text-3)",
                                  }}
                                >
                                  {(m.companyRole || "member").toUpperCase()}
                                </span>
                              </td>
                              <td style={{ padding: "12px 18px", textAlign: "right" }}>
                                <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", alignItems: "center" }}>
                                  {canViewOverview && m.status !== "invited" && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleOpenOverview(m)}
                                      style={{ gap: 4, display: "inline-flex", alignItems: "center", fontSize: 12, padding: "4px 8px" }}
                                    >
                                      <Eye size={12} /> Overview
                                    </Button>
                                  )}
                                  {canEdit && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleOpenEditMember(m)}
                                      style={{ fontSize: 12, padding: "4px 8px" }}
                                    >
                                      Edit
                                    </Button>
                                  )}
                                  {canEdit && !isSelf && (
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveMember(m)}
                                      title="Remove Member"
                                      style={{
                                        background: "none",
                                        border: "none",
                                        cursor: "pointer",
                                        color: "var(--gc-red, #ef4444)",
                                        padding: "4px 6px",
                                        borderRadius: 4,
                                        display: "inline-flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      <Trash size={13} />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Monday.com Connector Modal */}
          <Modal
            open={mondayModalOpen}
            onClose={() => setMondayModalOpen(false)}
            title="Connect Monday.com Documents & Procedures"
            width={640}
          >
            <div
              className="modal-body"
              style={{ display: "flex", flexDirection: "column", gap: 20 }}
            >
              {/* Step 1: Monday API Token */}
              <div
                style={{
                  background: "var(--surface-hover)",
                  border: "1px solid var(--border-soft)",
                  borderRadius: 12,
                  padding: 16,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 8,
                  }}
                >
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "var(--text-1)",
                    }}
                  >
                    1. Monday.com API Token
                  </span>
                  {mondayConnectedUser && (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#10B981",
                        background: "rgba(16, 185, 129, 0.1)",
                        padding: "2px 8px",
                        borderRadius: 10,
                      }}
                    >
                      <Check size={14} /> {mondayConnectedUser}
                    </span>
                  )}
                </div>
                <p
                  style={{
                    fontSize: 12,
                    color: "var(--text-3)",
                    margin: "0 0 10px",
                    lineHeight: 1.4,
                  }}
                >
                  In Monday.com: Click your profile avatar (bottom-left) →
                  Developers → Developer → Copy Personal API Token.
                </p>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    type="password"
                    placeholder="Paste your Monday Personal API Token…"
                    value={mondayApiKey}
                    onChange={(e) => setMondayApiKey(e.target.value)}
                    style={{
                      flex: 1,
                      padding: "8px 12px",
                      borderRadius: 8,
                      border: "1px solid var(--border-soft)",
                      background: "var(--surface)",
                      color: "var(--text-1)",
                      fontSize: 13,
                    }}
                  />
                  <button
                    type="button"
                    className="pill-btn primary"
                    disabled={mondayTesting || !mondayApiKey.trim()}
                    onClick={() => handleTestMonday()}
                    style={{ fontSize: 12 }}
                  >
                    {mondayTesting ? "Connecting…" : "Connect & Verify"}
                  </button>
                </div>
              </div>
              {/* Step 2: Import Options */}
              <div>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: "var(--text-1)",
                    marginBottom: 8,
                  }}
                >
                  2. Import Procedures or Knowledge
                </div>
                {/* Category Choice */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 14,
                  }}
                >
                  <span
                    style={{
                      fontSize: 12,
                      color: "var(--text-2)",
                      fontWeight: 600,
                    }}
                  >
                    Default Category:
                  </span>
                  <select
                    value={mondayCategory}
                    onChange={(e) => setMondayCategory(e.target.value as any)}
                    style={{
                      padding: "6px 10px",
                      borderRadius: 6,
                      border: "1px solid var(--border-soft)",
                      background: "var(--surface)",
                      color: "var(--text-1)",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    <option value="procedure">
                      📋 Company Procedure (SOP - Live Grounding)
                    </option>
                    <option value="knowledge">📚 General Knowledge Base</option>
                  </select>
                </div>
                {/* Mode A: Paste Direct Monday URL */}
                <div
                  style={{
                    padding: 14,
                    borderRadius: 10,
                    border: "1px solid var(--border-soft)",
                    background: "var(--surface)",
                    marginBottom: 14,
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "var(--text-1)",
                      marginBottom: 6,
                    }}
                  >
                    Option A: Paste Monday Workdoc or Board Link
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input
                      type="url"
                      placeholder="https://company.monday.com/docs/12345678 or .../boards/12345678"
                      value={mondayDirectUrl}
                      onChange={(e) => setMondayDirectUrl(e.target.value)}
                      style={{
                        flex: 1,
                        padding: "8px 12px",
                        borderRadius: 8,
                        border: "1px solid var(--border-soft)",
                        background: "var(--surface-hover)",
                        color: "var(--text-1)",
                        fontSize: 12,
                      }}
                    />
                    <button
                      type="button"
                      className="pill-btn primary"
                      disabled={importingMonday || !mondayDirectUrl.trim()}
                      onClick={() => handleImportMonday("url")}
                      style={{ fontSize: 12 }}
                    >
                      {importingMonday ? "Importing…" : "Import Link"}
                    </button>
                  </div>
                </div>
                {/* Mode B: Browse Docs & Boards */}
                <div
                  style={{
                    padding: 14,
                    borderRadius: 10,
                    border: "1px solid var(--border-soft)",
                    background: "var(--surface)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 10,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "var(--text-1)",
                      }}
                    >
                      Option B: Select from Monday Workdocs &amp; Boards
                    </div>
                    <button
                      type="button"
                      className="ghost-btn"
                      disabled={loadingMondayItems}
                      onClick={() => fetchMondayItems()}
                      style={{ fontSize: 11, padding: "3px 8px" }}
                    >
                      {loadingMondayItems ? "Refreshing…" : "↻ Refresh List"}
                    </button>
                  </div>
                  {loadingMondayItems ? (
                    <div
                      style={{
                        textAlign: "center",
                        padding: 20,
                        fontSize: 12,
                        color: "var(--text-3)",
                      }}
                    >
                      Loading Monday items…
                    </div>
                  ) : (
                    <>
                      {(Boolean(mondayConnectedUser) || Boolean(mondayApiKey) || mondayBoards.length > 0 || mondayDocs.length > 0) && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 10 }}>
                          <div style={{ position: "relative" }}>
                            <input
                              type="text"
                              placeholder="🔍 Search boards and docs by name (חיפוש לוח או מסמך)..."
                              value={mondaySearch}
                              onChange={(e) => setMondaySearch(e.target.value)}
                              style={{
                                width: "100%",
                                padding: "8px 12px",
                                paddingRight: mondaySearch ? 32 : 12,
                                borderRadius: 6,
                                border: "1px solid var(--border-soft)",
                                background: "var(--surface-hover)",
                                color: "var(--text-1)",
                                fontSize: 12.5,
                              }}
                            />
                            {mondaySearch && (
                              <button
                                type="button"
                                onClick={() => setMondaySearch("")}
                                style={{
                                  position: "absolute",
                                  right: 8,
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  background: "none",
                                  border: "none",
                                  color: "var(--text-3)",
                                  cursor: "pointer",
                                  fontSize: 14,
                                  padding: 4,
                                }}
                              >
                                ✕
                              </button>
                            )}
                          </div>
                          <div style={{ display: "flex", gap: 6, fontSize: 11 }}>
                            <button
                              type="button"
                              onClick={() => setMondayFilterType("all")}
                              className={`pill-btn ${mondayFilterType === "all" ? "primary" : ""}`}
                              style={{ padding: "2px 8px", fontSize: 11 }}
                            >
                              All ({mondayBoards.length + mondayDocs.length})
                            </button>
                            <button
                              type="button"
                              onClick={() => setMondayFilterType("boards")}
                              className={`pill-btn ${mondayFilterType === "boards" ? "primary" : ""}`}
                              style={{ padding: "2px 8px", fontSize: 11 }}
                            >
                              📊 Boards ({mondayBoards.length})
                            </button>
                            <button
                              type="button"
                              onClick={() => setMondayFilterType("docs")}
                              className={`pill-btn ${mondayFilterType === "docs" ? "primary" : ""}`}
                              style={{ padding: "2px 8px", fontSize: 11 }}
                            >
                              📝 Docs ({mondayDocs.length})
                            </button>
                          </div>
                        </div>
                      )}
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 8,
                          maxHeight: 340,
                          overflowY: "auto",
                        }}
                      >
                        {mondayDocs.length === 0 && mondayBoards.length === 0 ? (
                          <div
                            style={{
                              fontSize: 12,
                              color: "var(--text-3)",
                              textAlign: "center",
                              padding: 12,
                            }}
                          >
                            {mondayConnectedUser
                              ? "No docs or boards found. You can paste a direct Monday link above."
                              : "Connect your API token above to browse your Monday docs and boards."}
                          </div>
                        ) : filteredMondayDocs.length === 0 && filteredMondayBoards.length === 0 ? (
                          <div
                            style={{
                              fontSize: 12,
                              color: "var(--text-3)",
                              textAlign: "center",
                              padding: 16,
                            }}
                          >
                            No boards or docs found matching "{mondaySearch}".
                          </div>
                        ) : (
                          <>
                            {filteredMondayDocs.map((doc) => (
                              <div
                                key={doc.id}
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  padding: "8px 10px",
                                  borderRadius: 6,
                                  background: "var(--surface-hover)",
                                  fontSize: 12,
                                }}
                              >
                                <span
                                  style={{
                                    fontWeight: 600,
                                    color: "var(--text-1)",
                                  }}
                                >
                                  📝 {doc.title}
                                </span>
                                <button
                                  type="button"
                                  className="pill-btn primary"
                                  disabled={importingMonday}
                                  onClick={() =>
                                    handleImportMonday("doc", doc.id)
                                  }
                                  style={{ fontSize: 11, padding: "3px 10px" }}
                                >
                                  Import Doc
                                </button>
                              </div>
                            ))}
                            {filteredMondayBoards.map((b) => (
                              <div
                                key={b.id}
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  padding: "10px 14px",
                                  borderRadius: 8,
                                  background: "var(--surface-hover)",
                                  fontSize: 12.5,
                                  border: "1px solid var(--border-soft)",
                                  gap: 12,
                                }}
                              >
                                <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0, flex: 1 }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                    <span style={{ fontWeight: 600, color: "var(--text-1)" }}>📊 {b.name}</span>
                                    <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 4, background: "rgba(226, 74, 104, 0.12)", color: "#E24A68", fontWeight: 700 }}>
                                      Board #{b.id}
                                    </span>
                                  </div>
                                  {(b as any).description && (
                                    <div style={{ fontSize: 11, color: "var(--text-3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                      {(b as any).description}
                                    </div>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  className="pill-btn primary"
                                  disabled={importingMonday}
                                  onClick={() =>
                                    handleImportMonday("board", b.id)
                                  }
                                  style={{ fontSize: 11, padding: "4px 12px", flexShrink: 0 }}
                                >
                                  Import Board
                                </button>
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-foot">
              <button
                type="button"
                className="ghost-btn"
                onClick={() => setMondayModalOpen(false)}
              >
                Done
              </button>
            </div>
          </Modal>
          {/* SharePoint / OneDrive Connector Modal */}
          <Modal
            open={sharepointModalOpen}
            onClose={() => setSharepointModalOpen(false)}
            title="Import from SharePoint / Microsoft 365 / OneDrive"
            width={580}
          >
            <div
              className="modal-body"
              style={{ display: "flex", flexDirection: "column", gap: 16 }}
            >
              <p
                style={{
                  fontSize: 13,
                  color: "var(--text-2)",
                  margin: 0,
                  lineHeight: 1.5,
                }}
              >
                Import corporate SOPs, sales decks, or procedure files directly
                from SharePoint or OneDrive. SellSynq extracts text from Word
                (.docx), PDF (.pdf), PowerPoint (.pptx), Excel (.xlsx), and Text
                files to ground your live meeting AI.
              </p>
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "var(--text-1)",
                    marginBottom: 6,
                  }}
                >
                  SharePoint or OneDrive Document URL *
                </label>
                <input
                  type="url"
                  placeholder="https://company.sharepoint.com/:w:/s/... or https://1drv.ms/..."
                  value={sharepointUrl}
                  onChange={(e) => setSharepointUrl(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 8,
                    border: "1px solid var(--border-soft)",
                    background: "var(--surface-hover)",
                    color: "var(--text-1)",
                    fontSize: 13,
                  }}
                />
                <span
                  style={{
                    display: "block",
                    fontSize: 11,
                    color: "var(--text-3)",
                    marginTop: 4,
                  }}
                >
                  Supports standard sharing links ("Anyone with the link" or
                  organization sharing links).
                </span>
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "var(--text-1)",
                    marginBottom: 6,
                  }}
                >
                  Document Category
                </label>
                <select
                  value={sharepointCategory}
                  onChange={(e) => setSharepointCategory(e.target.value as any)}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    borderRadius: 8,
                    border: "1px solid var(--border-soft)",
                    background: "var(--surface-hover)",
                    color: "var(--text-1)",
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  <option value="procedure">
                    📋 Company Procedure (SOP - Live Grounding)
                  </option>
                  <option value="knowledge">
                    📚 General Knowledge Base Document
                  </option>
                </select>
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "var(--text-2)",
                    marginBottom: 4,
                  }}
                >
                  Microsoft / Azure Bearer Access Token (Optional)
                </label>
                <input
                  type="password"
                  placeholder="Optional: Enter access token if link requires corporate SSO authorization"
                  value={sharepointToken}
                  onChange={(e) => setSharepointToken(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    borderRadius: 8,
                    border: "1px solid var(--border-soft)",
                    background: "var(--surface-hover)",
                    color: "var(--text-1)",
                    fontSize: 12,
                  }}
                />
                <span
                  style={{
                    display: "block",
                    fontSize: 11,
                    color: "var(--text-3)",
                    marginTop: 3,
                  }}
                >
                  Leave empty for standard shared links.
                </span>
              </div>
            </div>
            <div className="modal-foot">
              <button
                type="button"
                className="ghost-btn"
                disabled={importingSharepoint}
                onClick={() => setSharepointModalOpen(false)}
              >
                Cancel
              </button>
              <Button
                type="button"
                variant="primary"
                disabled={importingSharepoint || !sharepointUrl.trim()}
                onClick={handleImportSharePoint}
                style={{ minWidth: 160 }}
              >
                {importingSharepoint
                  ? "Indexing Document…"
                  : "Import into Grounding"}
              </Button>
            </div>
          </Modal>

          {/* Invite Member Modal */}
          <Modal
            open={inviteModalOpen}
            onClose={() => {
              setInviteModalOpen(false);
              setInviteResult(null);
            }}
            title={inviteResult ? "Member Pre-Approved & Invited" : "Invite Member & Configure Permissions"}
            width={580}
          >
            <div className="modal-body" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {inviteResult ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 16, padding: "8px 0" }}>
                  <div
                    style={{
                      padding: 16,
                      borderRadius: 10,
                      background: "rgba(16, 185, 129, 0.1)",
                      border: "1px solid rgba(16, 185, 129, 0.3)",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 12,
                    }}
                  >
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        background: "#10b981",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        fontSize: 16,
                        flexShrink: 0,
                      }}
                    >
                      ✓
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, color: "#10b981", fontSize: 15, marginBottom: 4 }}>
                        Member Pre-Approved &amp; Invited!
                      </div>
                      <div style={{ fontSize: 13, color: "var(--text-1)", lineHeight: 1.5 }}>
                        <strong>{inviteResult.email}</strong> is automatically pre-approved. They can sign in immediately with Google — no administrator approval needed.
                      </div>
                      <div style={{ fontSize: 12, color: "var(--text-2)", marginTop: 6 }}>
                        {inviteResult.emailDispatched
                          ? "✓ System invitation email has been dispatched to their inbox."
                          : "ℹ️ You can also send the direct sign-in link below or via your email client."}
                      </div>
                    </div>
                  </div>

                  <div>
                    <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--text-2)", marginBottom: 6 }}>
                      Direct Workspace Sign-in Link
                    </label>
                    <div style={{ display: "flex", gap: 8 }}>
                      <input
                        type="text"
                        readOnly
                        value={inviteResult.inviteUrl}
                        style={{
                          flex: 1,
                          padding: "9px 12px",
                          borderRadius: 8,
                          border: "1px solid var(--border-soft)",
                          background: "var(--surface-hover)",
                          color: "var(--text-1)",
                          fontSize: 12,
                          fontFamily: "monospace",
                        }}
                      />
                      <Button
                        variant="primary"
                        onClick={() => {
                          void navigator.clipboard.writeText(inviteResult.inviteUrl);
                          setInviteResult((prev) => (prev ? { ...prev, copied: true } : null));
                          toast.push({ tone: "success", message: "Invite link copied to clipboard!" });
                        }}
                      >
                        {inviteResult.copied ? "Copied! ✓" : "Copy Link"}
                      </Button>
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 12 }}>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        const orgName = data?.organization?.name || "SellSynq";
                        const subject = encodeURIComponent(`Invitation to join ${orgName} on SellSynq`);
                        const body = encodeURIComponent(
                          `Hi,\n\nYou have been invited to join ${orgName} on SellSynq.\nYour access is pre-approved. Sign in here:\n${inviteResult.inviteUrl}\n\nWelcome!`
                        );
                        window.open(`mailto:${inviteResult.email}?subject=${subject}&body=${body}`, "_blank");
                      }}
                    >
                      ✉️ Open in Email Client
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setInviteEmail("");
                        setInviteName("");
                        setInviteResult(null);
                      }}
                    >
                      Invite Another
                    </Button>
                    <Button
                      variant="primary"
                      onClick={() => {
                        setInviteModalOpen(false);
                        setInviteEmail("");
                        setInviteName("");
                        setInviteResult(null);
                      }}
                    >
                      Done
                    </Button>
                  </div>
                </div>
              ) : (
                <>
<p style={{ fontSize: 13, color: "var(--text-2)", margin: 0 }}>
                Invite any team member or contractor (even external email domains). Set their role, reporting manager, and which data connectors they can access.
              </p>

              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--text-2)", marginBottom: 6 }}>
                  Email Address * (Any domain allowed)
                </label>
                <input
                  type="email"
                  placeholder="e.g. colleague@company.com or advisor@agency.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "9px 12px",
                    borderRadius: 8,
                    border: "1px solid var(--border-soft)",
                    background: "var(--surface-hover)",
                    color: "var(--text-1)",
                    fontSize: 13,
                  }}
                />
              </div>

              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--text-2)", marginBottom: 6 }}>
                  Full Name (Optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g. Sarah Connor"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "9px 12px",
                    borderRadius: 8,
                    border: "1px solid var(--border-soft)",
                    background: "var(--surface-hover)",
                    color: "var(--text-1)",
                    fontSize: 13,
                  }}
                />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--text-2)", marginBottom: 6 }}>
                    Coaching Role
                  </label>
                  <select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "9px 10px",
                      borderRadius: 8,
                      border: "1px solid var(--border-soft)",
                      background: "var(--surface-hover)",
                      color: "var(--text-1)",
                      fontSize: 13,
                    }}
                  >
                    {(data?.organization?.roles?.length ? data.organization.roles : DEFAULT_COMPANY_ROLES).map((r) => (
                      <option key={r} value={r}>
                        {cleanRoleName(r)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--text-2)", marginBottom: 6 }}>
                    Access Level
                  </label>
                  <select
                    value={inviteCompanyRole}
                    onChange={(e) => setInviteCompanyRole(e.target.value as "admin" | "member")}
                    style={{
                      width: "100%",
                      padding: "9px 10px",
                      borderRadius: 8,
                      border: "1px solid var(--border-soft)",
                      background: "var(--surface-hover)",
                      color: "var(--text-1)",
                      fontSize: 13,
                    }}
                  >
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>

              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--text-2)", marginBottom: 6 }}>
                  Reports To (Manager)
                </label>
                <select
                  value={inviteManagerUid}
                  onChange={(e) => setInviteManagerUid(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "9px 10px",
                    borderRadius: 8,
                    border: "1px solid var(--border-soft)",
                    background: "var(--surface-hover)",
                    color: "var(--text-1)",
                    fontSize: 13,
                  }}
                >
                  <option value="">None (Top Level / Direct Executive)</option>
                  {data?.members.map((m) => (
                    <option key={m.uid} value={m.uid}>
                      {m.name || m.email} ({cleanRoleName(m.functionalRole || m.role || "Member")})
                    </option>
                  ))}
                </select>
                <span style={{ fontSize: 11, color: "var(--text-3)", marginTop: 4, display: "block" }}>
                  The selected manager will have full oversight of this employee's open tasks and meetings.
                </span>
              </div>

              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--text-2)", marginBottom: 8 }}>
                  Permitted Data Connectors
                </label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  {[
                    { id: "uploads", label: "📁 File & Web Uploads" },
                    { id: "googleDrive", label: "🔷 Google Drive" },
                    { id: "monday", label: "🔴 Monday.com" },
                    { id: "sharepoint", label: "🟦 SharePoint & OneDrive" },
                  ].map((conn) => {
                    const isChecked = inviteConnectors.includes(conn.id as ConnectorType);
                    return (
                      <label
                        key={conn.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "8px 12px",
                          borderRadius: 8,
                          background: isChecked ? "rgba(99, 102, 241, 0.08)" : "var(--surface-hover)",
                          border: isChecked ? "1px solid var(--primary)" : "1px solid var(--border-soft)",
                          cursor: "pointer",
                          fontSize: 12,
                          color: "var(--text-1)",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setInviteConnectors([...inviteConnectors, conn.id as ConnectorType]);
                            } else {
                              setInviteConnectors(inviteConnectors.filter((c) => c !== conn.id));
                            }
                          }}
                        />
                        {conn.label}
                      </label>
                    );
                  })}
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 12 }}>
                <Button variant="ghost" onClick={() => setInviteModalOpen(false)}>
                  Cancel
                </Button>
                <Button variant="primary" disabled={invitingMember || !inviteEmail.trim()} onClick={handleInviteMember}>
                  {invitingMember ? "Inviting…" : "Send Invitation"}
                </Button>
              </div>
                </>
              )}
            </div>
          </Modal>

          {/* Edit Member Modal */}
          <Modal
            open={editMemberModalOpen}
            onClose={() => setEditMemberModalOpen(false)}
            title="Edit Member Permissions & Hierarchy"
            width={580}
          >
            <div className="modal-body" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {editingMember && (
                <>
                  <div
                    style={{
                      background: "var(--surface-hover)",
                      padding: 12,
                      borderRadius: 10,
                      border: "1px solid var(--border-soft)",
                    }}
                  >
                    <div style={{ fontWeight: 600, color: "var(--text-1)", fontSize: 14 }}>
                      {editingMember.name || editingMember.email}
                    </div>
                    <div style={{ color: "var(--text-3)", fontSize: 12 }}>{editingMember.email}</div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div>
                      <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--text-2)", marginBottom: 6 }}>
                        Coaching Role
                      </label>
                      <select
                        value={editMemberRole}
                        onChange={(e) => setEditMemberRole(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "9px 10px",
                          borderRadius: 8,
                          border: "1px solid var(--border-soft)",
                          background: "var(--surface-hover)",
                          color: "var(--text-1)",
                          fontSize: 13,
                        }}
                      >
                        {(data?.organization?.roles?.length ? data.organization.roles : DEFAULT_COMPANY_ROLES).map((r) => (
                          <option key={r} value={r}>
                            {cleanRoleName(r)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--text-2)", marginBottom: 6 }}>
                        Access Level
                      </label>
                      <select
                        value={editMemberCompanyRole}
                        onChange={(e) => setEditMemberCompanyRole(e.target.value as "admin" | "member")}
                        style={{
                          width: "100%",
                          padding: "9px 10px",
                          borderRadius: 8,
                          border: "1px solid var(--border-soft)",
                          background: "var(--surface-hover)",
                          color: "var(--text-1)",
                          fontSize: 13,
                        }}
                      >
                        <option value="member">Member</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--text-2)", marginBottom: 6 }}>
                      Reports To (Manager)
                    </label>
                    <select
                      value={editMemberManagerUid}
                      onChange={(e) => setEditMemberManagerUid(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "9px 10px",
                        borderRadius: 8,
                        border: "1px solid var(--border-soft)",
                        background: "var(--surface-hover)",
                        color: "var(--text-1)",
                        fontSize: 13,
                      }}
                    >
                      <option value="">None (Top Level / Direct Executive)</option>
                      {data?.members
                        .filter((m) => m.uid !== editingMember.uid)
                        .map((m) => (
                          <option key={m.uid} value={m.uid}>
                            {m.name || m.email} ({cleanRoleName(m.functionalRole || m.role || "Member")})
                          </option>
                        ))}
                    </select>
                    <span style={{ fontSize: 11, color: "var(--text-3)", marginTop: 4, display: "block" }}>
                      The assigned manager will see open action items, tasks, and meetings conducted by this employee.
                    </span>
                  </div>

                  <div>
                    <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--text-2)", marginBottom: 8 }}>
                      Permitted Data Connectors
                    </label>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      {[
                        { id: "uploads", label: "📁 File & Web Uploads" },
                        { id: "googleDrive", label: "🔷 Google Drive" },
                        { id: "monday", label: "🔴 Monday.com" },
                        { id: "sharepoint", label: "🟦 SharePoint & OneDrive" },
                      ].map((conn) => {
                        const isChecked = editMemberConnectors.includes(conn.id as ConnectorType);
                        return (
                          <label
                            key={conn.id}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              padding: "8px 12px",
                              borderRadius: 8,
                              background: isChecked ? "rgba(99, 102, 241, 0.08)" : "var(--surface-hover)",
                              border: isChecked ? "1px solid var(--primary)" : "1px solid var(--border-soft)",
                              cursor: "pointer",
                              fontSize: 12,
                              color: "var(--text-1)",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setEditMemberConnectors([...editMemberConnectors, conn.id as ConnectorType]);
                                } else {
                                  setEditMemberConnectors(editMemberConnectors.filter((c) => c !== conn.id));
                                }
                              }}
                            />
                            {conn.label}
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 12 }}>
                    <Button variant="ghost" onClick={() => setEditMemberModalOpen(false)}>
                      Cancel
                    </Button>
                    <Button variant="primary" disabled={savingMember} onClick={handleSaveMember}>
                      {savingMember ? "Saving…" : "Save Changes"}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </Modal>

          {/* Member Overview Modal (Manager Oversight Drill-down) */}
          <Modal
            open={overviewModalOpen}
            onClose={() => setOverviewModalOpen(false)}
            title={overviewMember ? `Employee Overview: ${overviewMember.name || overviewMember.email}` : "Employee Overview"}
            width={720}
          >
            <div className="modal-body" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              {overviewLoading ? (
                <div style={{ padding: 40, textAlign: "center", color: "var(--text-2)" }}>
                  Loading employee performance overview...
                </div>
              ) : overviewData ? (
                <>
                  {/* Top Stats Cards */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                    <div
                      style={{
                        background: "var(--surface-hover)",
                        border: "1px solid var(--border-soft)",
                        borderRadius: 12,
                        padding: "14px 16px",
                      }}
                    >
                      <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-3)", textTransform: "uppercase" }}>
                        Open Tasks
                      </div>
                      <div style={{ fontSize: 24, fontWeight: 700, color: "#EF4444", marginTop: 4 }}>
                        {overviewData.stats.openTasksCount}
                      </div>
                    </div>

                    <div
                      style={{
                        background: "var(--surface-hover)",
                        border: "1px solid var(--border-soft)",
                        borderRadius: 12,
                        padding: "14px 16px",
                      }}
                    >
                      <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-3)", textTransform: "uppercase" }}>
                        Completed Tasks
                      </div>
                      <div style={{ fontSize: 24, fontWeight: 700, color: "#10B981", marginTop: 4 }}>
                        {overviewData.stats.completedTasksCount}
                      </div>
                    </div>

                    <div
                      style={{
                        background: "var(--surface-hover)",
                        border: "1px solid var(--border-soft)",
                        borderRadius: 12,
                        padding: "14px 16px",
                      }}
                    >
                      <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-3)", textTransform: "uppercase" }}>
                        Meetings Conducted
                      </div>
                      <div style={{ fontSize: 24, fontWeight: 700, color: "var(--primary)", marginTop: 4 }}>
                        {overviewData.stats.totalMeetingsCount}
                      </div>
                    </div>
                  </div>

                  {/* Tasks Section */}
                  <div>
                    <h4 style={{ fontSize: 14, fontWeight: 700, color: "var(--text-1)", margin: "0 0 10px" }}>
                      Action Items &amp; Tasks ({overviewData.tasks.length})
                    </h4>
                    {overviewData.tasks.length === 0 ? (
                      <div style={{ fontSize: 13, color: "var(--text-3)", padding: "12px 0" }}>
                        No action items recorded for this employee yet.
                      </div>
                    ) : (
                      <div
                        style={{
                          background: "var(--surface)",
                          border: "1px solid var(--border-soft)",
                          borderRadius: 10,
                          overflow: "hidden",
                          maxHeight: 220,
                          overflowY: "auto",
                        }}
                      >
                        {overviewData.tasks.map((task) => (
                          <div
                            key={task.taskId}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                              padding: "10px 14px",
                              borderBottom: "1px solid var(--border-soft)",
                              background: task.done ? "var(--surface-hover)" : "transparent",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={task.done}
                              onChange={() => handleToggleOverviewTask(task)}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div
                                style={{
                                  fontSize: 13,
                                  fontWeight: 500,
                                  color: task.done ? "var(--text-3)" : "var(--text-1)",
                                  textDecoration: task.done ? "line-through" : "none",
                                }}
                              >
                                {task.what}
                              </div>
                              <div style={{ fontSize: 11, color: "var(--text-3)", display: "flex", gap: 8, marginTop: 2 }}>
                                <span>Client: {task.client}</span>
                                {task.due && <span>Due: {task.due}</span>}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Meetings Section */}
                  <div>
                    <h4 style={{ fontSize: 14, fontWeight: 700, color: "var(--text-1)", margin: "0 0 10px" }}>
                      Recent Meetings &amp; Activity ({overviewData.meetings.length})
                    </h4>
                    {overviewData.meetings.length === 0 ? (
                      <div style={{ fontSize: 13, color: "var(--text-3)", padding: "12px 0" }}>
                        No meetings conducted yet.
                      </div>
                    ) : (
                      <div
                        style={{
                          background: "var(--surface)",
                          border: "1px solid var(--border-soft)",
                          borderRadius: 10,
                          overflow: "hidden",
                          maxHeight: 200,
                          overflowY: "auto",
                        }}
                      >
                        {overviewData.meetings.map((m) => (
                          <div
                            key={m.id}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              padding: "10px 14px",
                              borderBottom: "1px solid var(--border-soft)",
                              fontSize: 13,
                            }}
                          >
                            <div>
                              <div style={{ fontWeight: 600, color: "var(--text-1)" }}>{m.title}</div>
                              <div style={{ fontSize: 11, color: "var(--text-3)" }}>
                                {m.client} • {new Date(m.date).toLocaleDateString()}
                              </div>
                            </div>
                            {m.stage && (
                              <span
                                style={{
                                  fontSize: 11,
                                  padding: "2px 8px",
                                  borderRadius: 8,
                                  background: "var(--surface-hover)",
                                  color: "var(--text-2)",
                                }}
                              >
                                {m.stage}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              ) : null}
            </div>
          </Modal>
        </ErrorBoundary>
      </main>
    </div>
  );
}
